import type { PracticaDefinition } from "../types";

export const practicas: PracticaDefinition[] = [
  {
    id: "practica1",
    title: "Práctica 1",
    subtitle: "Registro de visitas",
    fullTitle: "Práctica 1: Domina Excel con 5 escenarios reales",
    description:
      "¡Bienvenido! En las próximas secciones aplicarás las funciones y flujos que usan los analistas profesionales día a día.",
    requirements: [
      "<strong>Microsoft 365</strong> o <strong>Excel 2024</strong> (Windows, Mac o Excel en la web).",
      "Conocimientos básicos: abrir libros, seleccionar rangos y usar fórmulas simples.",
      'Complemento <strong>"Modo Imparable"</strong> instalado y activo.',
    ],
    projects: [
      /* ═══════════════════════  PROYECTO 1  ═══════════════════════ */
      {
        id: "project1",
        title: "Proyecto 1: Operación Ecoturismo",
        subtitle: "Registro de asistencia de visitantes",
        description:
          "Trabajas para el Servicio de Parques Nacionales.\n\nEstás creando un libro de Excel para ayudar a analizar la asistencia de visitantes y las actividades dirigidas por guardaparques.",
        topics: [
          "Crear tabla de registro",
          "Filtrar por fechas",
          "Contar visitas por tipo",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Calcular asistencia promedio",
            description:
              "En la celda <strong>C12</strong> de la hoja <strong>Asistencia de Visitantes</strong>, usa referencias estructuradas para calcular el <strong>promedio</strong> de asistencia.",
            solution: [
              "En la hoja de trabajo <strong>Asistencia de Visitantes</strong>, haz clic en la celda <span class=\"formula\">C12</span> para seleccionarla.",
              "En la barra de fórmulas, escribe <span class=\"formula\">=PROMEDIO</span> y presiona <span class=\"formula\">Tab</span>.",
              "Selecciona el rango de celdas <span class=\"formula\">B4:G8</span> con el mouse para usar referencias estructuradas.",
              "Presiona <span class=\"formula\">Enter</span> para completar la fórmula.",
            ],
            expectedResult: "97614.43",
            finalFormula: "=PROMEDIO(Asistencia[[Enero]:[Junio]])",
          },
          {
            id: 2,
            title: "Tarea 2: Ampliar meses de Asistencia",
            description:
              "Utilizando la opción <strong>Rellenar serie en Excel</strong>, amplíe la tabla Asistencia para incluir los meses restantes del año de Julio a Diciembre.",
            solution: [
              "En la hoja de trabajo <span class=\"formula\">Asistencia de Visitantes</span>, haga clic en la celda <span class=\"formula\">G3</span> para seleccionarla.",
              "Arrastra el <strong>Controlador de relleno</strong> (esquina inferior derecha de la selección) hacia la derecha hasta la celda <span class=\"formula\">M3</span>. De esta forma agregas automáticamente los meses de <strong>Julio</strong> a <strong>Diciembre</strong>.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Fórmula condicional en Actividades-Guardabosques",
            description:
              'En la celda <strong>F4</strong> de la hoja <strong>Actividades-Guardabosques</strong>, construye una fórmula que evalúe si el Parque Nacional ofrece ambos Programas Juveniles y Programa de Astronomía, y de lo contrario devuelva <strong>No</strong>. Arrastra el controlador de relleno hasta <strong>F8</strong>.',
            solution: [
              "En la hoja <strong>Actividades-Guardabosques</strong>, haz clic en la celda <span class=\"formula\">F4</span>.",
              "En la barra de fórmulas escribe <span class=\"formula\">=NO</span> y presiona <span class=\"formula\">Tab</span>. Haz clic en <strong>fx</strong>.",
              "En el asistente ingresa: <strong>Valor_lógico:</strong> <span class=\"formula\">B4=\"Ninguno\"</span>. Haz clic en Aceptar.",
              "Corta la fórmula. Escribe <span class=\"formula\">=Y</span> y presiona Tab. Haz clic en <strong>fx</strong>.",
              "Ingresa: <strong>Valor_lógico1:</strong> <span class=\"formula\">NO(B4=\"Ninguno\")</span>, <strong>Valor_lógico2:</strong> <span class=\"formula\">E4=\"Si\"</span>. Aceptar.",
              "Corta la fórmula. Escribe <span class=\"formula\">=SI</span> y presiona Tab. Haz clic en <strong>fx</strong>.",
              "Ingresa: <strong>Prueba_lógica:</strong> <span class=\"formula\">Y(NO(B4=\"Ninguno\"), E4=\"Si\")</span>, <strong>Valor_si_verdadero:</strong> <span class=\"formula\">\"Si\"</span>, <strong>Valor_si_falso:</strong> <span class=\"formula\">\"No\"</span>.",
              "Arrastra el <strong>Controlador de relleno</strong> desde F4 hasta F8.",
            ],
            finalFormula: '=SI(Y(NO(B4="Ninguno"); E4="Si");"Si";"No")',
          },
          {
            id: 4,
            title: "Tarea 4: Resaltar valores ≥ 200,000",
            description:
              "En la hoja <strong>Asistencia de Visitantes</strong>, selecciona el rango <strong>B4:G8</strong> y aplica formato condicional que resalte celdas con valores ≥ <strong>200,000</strong> con fondo <strong>amarillo</strong>.",
            solution: [
              "Abre la hoja <strong>Asistencia de Visitantes</strong>.",
              "Selecciona el rango de datos <strong>B4:G8</strong>.",
              "En la pestaña <strong>Inicio</strong>, haz clic en <strong>Formato condicional</strong>.",
              "Elige <strong>Resaltar reglas de celdas > Mayor o igual que…</strong>",
              "Escribe <strong>200000</strong> y elige un relleno amarillo.",
              "Haz clic en <strong>Aceptar</strong>.",
            ],
            expectedResult:
              "Todas las celdas con valores ≥ 200,000 deben verse resaltadas en amarillo.",
          },
          {
            id: 5,
            title: "Tarea 5: Esquema en la tabla dinámica",
            description:
              "En la tabla dinámica <strong>Programas por Parque</strong>, cambia el diseño para que se muestre en formato de esquema.",
            solution: [
              "En la hoja <strong>Programas por Parque</strong>, haz clic en la tabla dinámica para mostrar <strong>Herramientas de tabla dinámica</strong>.",
              "En la pestaña <strong>Diseño</strong>, grupo <strong>Diseño</strong>, selecciona <strong>Diseño de informe</strong> y haz clic en <strong>Mostrar en formato de esquema</strong>.",
            ],
          },
        ],
      },
      /* ═══════════════════════  PROYECTO 2  ═══════════════════════ */
      {
        id: "project2",
        title: "Proyecto 2: Mercado Local",
        subtitle: "Inventario y ventas del mercado",
        description:
          "Usted es el Director de Ventas de su Asociación de Mercado de Agricultores Locales.\n\nEstá preparando un libro de Excel para ayudarlo a analizar los ingresos de la temporada pasada y hacer proyecciones para la próxima.",
        topics: [
          "Análisis de hipótesis",
          "Uso de Subtotal",
          "Campos Calculados",
        ],
        tasks: [
          {
            id: 1,
            title: 'Tarea 1: Nueva cuota con "Buscar objetivo"',
            description:
              "En la hoja <strong>Ingresos de Alquiler</strong>, utilice una función de previsión en la celda <strong>H7</strong> para calcular una nueva cuota de alquiler que resultará en un Ingreso total proyectado de <strong>$160,000</strong>.",
            solution: [
              "En la hoja <strong>Ingresos de Alquiler</strong>, seleccione la celda <strong>H7</strong>.",
              "En la pestaña <strong>Datos</strong>, grupo <strong>Previsión</strong>, haga clic en <strong>Análisis de hipótesis</strong> y seleccione <strong>Buscar objetivo…</strong>",
              "Configure: <strong>Definir la celda:</strong> H7, <strong>Con el valor:</strong> 160000, <strong>Cambiando la celda:</strong> H6.",
              "Haga clic en <strong>Aceptar</strong> dos veces. (H7 = $160,000 y H6 = $22.99).",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Subtotal de Ventas por Ubicación",
            description:
              "En la hoja <strong>Mercado de Agricultores</strong>, inserta un <strong>Subtotal</strong> que calcule la <strong>Suma</strong> en la columna <strong>Ventas</strong> para cada <strong>Ubicación</strong>.",
            solution: [
              "En la hoja <strong>Mercado de Agricultores</strong>, selecciona el rango <span class=\"formula\">A2:E86</span>.",
              "En la pestaña <strong>Datos</strong>, grupo <strong>Esquema</strong>, haz clic en <strong>Subtotal</strong>.",
              "Configura: <strong>Para cada cambio en:</strong> <span class=\"formula\">Ubicación</span>, <strong>Usar función:</strong> <span class=\"formula\">Suma</span>, <strong>Agregar subtotal a:</strong> <span class=\"formula\">Ventas</span>.",
              "Deja los demás valores predeterminados y haz clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Rastrear precedentes y corregir fórmula",
            description:
              "En la hoja <strong>Ingresos de Alquiler</strong>, rastrea los precedentes de la celda <strong>C7</strong> para encontrar el error y corrige la fórmula.",
            solution: [
              "En la hoja <strong>Ingresos de Alquiler</strong>, haz clic en la celda <span class=\"formula\">C7</span>.",
              "En la pestaña <strong>Fórmulas</strong>, grupo <strong>Auditoría de fórmulas</strong>, haz clic en <strong>Rastrear precedentes</strong>.",
              "Observa que la flecha omite la celda <span class=\"formula\">C5</span>, indicando que no se usa en el cálculo.",
              "Edita la fórmula en <span class=\"formula\">C7</span> a: <span class=\"formula\">=C4*C5*C6</span>",
            ],
            finalFormula: "=C4*C5*C6",
          },
          {
            id: 4,
            title: "Tarea 4: Agrupar y ocultar columnas B:G",
            description:
              "En la hoja <strong>Ingresos de Alquiler</strong>, crea un grupo que incluya las columnas <strong>B a G</strong> y manténlas ocultas.",
            solution: [
              "En la hoja <strong>Ingresos de Alquiler</strong>, selecciona las columnas <span class=\"formula\">B:G</span>.",
              "En la pestaña <strong>Datos</strong>, grupo <strong>Esquema</strong>, haz clic en <strong>Agrupar</strong>.",
              "Con el grupo creado, haz clic en el signo '−' para dejar las columnas agrupadas y ocultas.",
            ],
          },
          {
            id: 5,
            title: 'Tarea 5: Campo calculado "Aumento del 3 %"',
            description:
              "En la hoja <strong>Ventas Proyectadas (Próx. año)</strong>, agrega un campo calculado <strong>Aumento del 3 %</strong> que proyecte las Ventas incrementándolas un 3 %.",
            solution: [
              "En la hoja <strong>Ventas Proyectadas (Próx. año)</strong>, haz clic dentro de la Tabla dinámica.",
              "En la pestaña <strong>Análisis de tabla dinámica</strong>, grupo <strong>Cálculos</strong>, haz clic en <strong>Campos, elementos y conjuntos</strong> y elige <strong>Campo calculado…</strong>",
              "Configura: <strong>Nombre:</strong> Aumento del 3 %, <strong>Fórmula:</strong> <span class=\"formula\">=(Ventas*3%)+Ventas</span>.",
              "Haz clic en <strong>Aceptar</strong>.",
            ],
            finalFormula: "=(Ventas*3%)+Ventas",
          },
        ],
      },
      /* ═══════════════════════  PROYECTO 3  ═══════════════════════ */
      {
        id: "project3",
        title: "Proyecto 3: Resort de Montaña",
        subtitle: "Escuela de esquí y precios en Utah",
        description:
          "Trabaja para la Asociación de Esquí de Utah.\n\nEstás creando un libro de Excel para analizar los precios de los pases de elevación y las inscripciones en las escuelas de esquí.",
        topics: [
          "Validación de Datos",
          "Suma Avanzada",
          "Gráfico Combinado",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Agrega validación de datos",
            description:
              "Agrega validación de datos en la hoja <strong>Inscripciones Escuela de Esquí</strong> en el rango <strong>C6:C15</strong>, de tipo lista, usando el rango E6:E9 como origen.",
            solution: [
              "<strong>Selecciona</strong> el rango <strong>C6:C15</strong> y dirígete a la pestaña <strong>Datos</strong>.",
              "En el grupo <strong>Herramientas de datos</strong>, haz clic en <strong>Validación de datos</strong>.",
              "Configura: <strong>Permitir:</strong> Lista, <strong>Origen:</strong> <code>=$E$6:$E$9</code>. Verifica que <strong>Desplegable en celda</strong> esté activada.",
              "Haz clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Sumar total de pases anuales",
            description:
              "En la celda <strong>O3</strong> de la hoja <strong>Precios de Esquí en Utah</strong>, suma el Total de Pases Anuales de Adultos para Complejos con Más de 400 Pulgadas de Nieve y Más de 1000 Hectáreas.",
            solution: [
              "La función en la celda O3 debe quedar así:",
            ],
            finalFormula: '=SUMAR.SI.CONJUNTO(E3:E16;G3:G16;">400";H3:H16;">1000")',
          },
          {
            id: 3,
            title: "Tarea 3: Calcular precio promedio de pase anual",
            description:
              "En la celda <strong>L3</strong> de <strong>Precios de Esquí en Utah</strong>, calcule el precio promedio de un Pase anual para complejos que operan más de 15 ascensores y 2,000 hectáreas.",
            solution: [
              "En la hoja Precios de Esquí en Utah, haga clic en la celda L3.",
              "En la Barra de fórmulas escriba <span class=\"formula\">=PROMEDIO.SI.CONJUNTO</span>, pulse Tab.",
              "Haga clic en fx. Ingrese: Rango_promedio: E6:E19, Rango_criterios1: H6:H19, Criterio1: >2000, Rango_criterios2: I6:I19, Criterio2: >15.",
              "Haga clic en <strong>Aceptar</strong>. (Resultado: $1,129.50).",
            ],
            expectedResult: "$1,129.50",
            finalFormula: '=PROMEDIO.SI.CONJUNTO(E6:E19,H6:H19,">2000",I6:I19,">15")',
          },
          {
            id: 4,
            title: "Tarea 4: Configurar cálculo manual",
            description:
              "Activa el <strong>modo de cálculo manual</strong> en Excel. Modifica H19 a <strong>2</strong> en la hoja <strong>Precios de Esquí en Utah</strong>. Si I19 no se actualiza automáticamente, lo hiciste correctamente.",
            solution: [
              "Haz clic en <strong>Archivo → Opciones</strong>.",
              "En <strong>Fórmulas</strong>, busca <strong>Modo de cálculo de libro</strong>.",
              "Selecciona <strong>Manual</strong>.",
              "Activa <strong>Recalcular antes de guardar</strong>.",
              "Haz clic en <strong>Aceptar</strong>. Usa F9 para recalcular manualmente.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Crear gráfico combinado",
            description:
              "Con los datos de <strong>Precios de Esquí en Utah</strong>, agrega un gráfico con Pase anual de adultos como gráfico de área y Pase de niños como línea. Coloca en celda <strong>A22</strong>.",
            solution: [
              "Seleccione las celdas B2:B16. Mantén Ctrl y selecciona E2:F16.",
              "En la pestaña <strong>Insertar</strong>, grupo <strong>Gráficos</strong>, haga clic en <strong>Insertar gráfico combinado</strong>.",
              "Configure: Pase anual de adultos → Áreas, Pase anual de niños → Líneas (eje secundario: habilitado).",
              "Haga clic en <strong>Aceptar</strong>.",
              "Arrastre el gráfico para que la esquina superior izquierda quede en la celda <strong>A22</strong>.",
            ],
          },
        ],
      },
      /* ═══════════════════════  PROYECTO 4  ═══════════════════════ */
      {
        id: "project4",
        title: "Proyecto 4: Logística de Exportación",
        subtitle: "Departamento de ventas y exportaciones",
        description:
          "Trabaja para una empresa de importación/exportación.\n\nEstá preparando un libro de Excel para analizar la exportación de granos por parte de varios países del mundo.",
        topics: [
          "Segmentación De Datos",
          "Uso Tablas Dinámicas",
          "Funciones de Fecha",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Crea una Segmentación de Datos",
            description:
              "En la hoja <strong>Exportaciones de Grano</strong>, cree una segmentación de datos para el campo <strong>País</strong> y seleccione únicamente <strong>Brasil</strong>.",
            solution: [
              "En la hoja <strong>Exportaciones de Grano</strong>, haga clic en cualquier celda de la tabla.",
              "Vaya a <strong>Diseño De Tabla → Insertar Segmentación de datos</strong>.",
              "Active la casilla <strong>País</strong> y pulse <strong>Aceptar</strong>.",
              "En la segmentación, haga clic en <strong>Brasil</strong> para que quede como único filtro activo.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Crear direcciones de correo electrónico",
            description:
              "En la celda <strong>C3</strong> de la hoja <strong>Departamento de Ventas</strong>, cree una dirección de correo (ej: sam.hart@fusiontomo.com). Use <strong>Relleno rápido</strong> para el resto.",
            solution: [
              "En la hoja <strong>Departamento de Ventas</strong>, haga clic en la celda <strong>C3</strong>.",
              "Escriba <strong>sam.hart@fusiontomo.com</strong>.",
              "Haga clic en la celda <strong>C4</strong>.",
              "En <strong>Inicio → Edición → Rellenar → Relleno rápido</strong>.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Calcular fecha estimada de llegada",
            description:
              "En la celda <strong>B6</strong> de la hoja <strong>Calculadora de Envío</strong>, inserte la función <strong>DIA.LAB</strong> para calcular la Fecha estimada de llegada.",
            solution: [
              "En la hoja <strong>Calculadora de Envío</strong>, haga clic en la celda <strong>B6</strong>.",
              "En la Barra de fórmulas, escriba <strong>=DIA.LAB</strong> y presione <strong>Tab</strong>.",
              "Haga clic en <strong>fx</strong>. Ingrese: <strong>Fecha inicial:</strong> B3, <strong>Días:</strong> B4, <strong>Vacaciones:</strong> B5.",
              "Haga clic en <strong>Aceptar</strong>.",
            ],
            finalFormula: "=DIA.LAB(B3,B4,B5)",
          },
          {
            id: 4,
            title: "Tarea 4: Número de pagos requeridos",
            description:
              "En la celda <strong>B8</strong> de <strong>Calculadora de Pago</strong>, utilice la función <strong>NPER</strong> para calcular cuántos pagos se requieren.",
            solution: [
              "En la hoja <strong>Calculadora de Pago</strong>, haga clic en <strong>B8</strong>.",
              "Escriba <strong>=NPER</strong> y presione <strong>Tab</strong>. Haga clic en <strong>fx</strong>.",
              "Ingrese: <strong>Tasa:</strong> B4/12, <strong>Pago:</strong> -B5, <strong>Va:</strong> B3, <strong>Vf:</strong> 0, <strong>Tipo:</strong> 0.",
              "Haga clic en <strong>Aceptar</strong>. (Resultado: 130 meses).",
            ],
            expectedResult: "130 meses",
            finalFormula: "=NPER(B4/12,-B5,B3,0)",
          },
          {
            id: 5,
            title: "Tarea 5: Crear Tabla dinámica",
            description:
              "Cree una <strong>Tabla dinámica</strong> en una nueva hoja llamada <strong>TablaDinámica</strong> que muestre la cantidad de grano exportada por cada país. Use los datos de <strong>Exportaciones de Grano</strong>.",
            solution: [
              "En <strong>Exportaciones de grano</strong>, haga clic en cualquier celda de la tabla.",
              "En <strong>Insertar → Tablas → Tabla dinámica</strong>. Acepte valores predeterminados y <strong>Aceptar</strong>.",
              "Arrastre <strong>País</strong> al área <strong>Filas</strong>.",
              "Arrastre <strong>Toneladas métricas</strong> al área <strong>Valores</strong>.",
              "Renombre la pestaña Hoja 1 a <strong>TablaDinámica</strong>.",
              "Arrastre la pestaña hacia la derecha.",
            ],
          },
        ],
      },
      /* ═══════════════════════  PROYECTO 5  ═══════════════════════ */
      {
        id: "project5",
        title: "Proyecto 5: Expo Submarina",
        subtitle: "Gestión de expo de buceo",
        description:
          "Usted es asistente administrativo de una asociación internacional de buceo.\n\nEstá preparando un libro de Excel para rastrear los expositores y las sesiones para una Convención de buceo anual.",
        topics: [
          "Quitar Duplicados",
          "Funciones de fecha",
          "Uso de Gráfico Dinámico",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Proteger Hoja",
            description:
              "En la hoja <strong>Principales Patrocinadores</strong>, protege la hoja con la contraseña <strong>12345</strong>.",
            solution: [
              "En la pestaña <strong>Revisar</strong>, grupo <strong>Proteger</strong>, haga clic en <strong>Proteger Hoja…</strong>",
              "Acepte los ajustes predeterminados e introduzca la contraseña <strong>12345</strong>.",
              "Confirme la contraseña escribiendo <strong>12345</strong> nuevamente.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Formato condicional con relleno",
            description:
              'En la hoja <strong>Expositores</strong>, agrega formato condicional a la columna <strong>Nombre del Expositor</strong> que aplique relleno amarillo si la industria es <strong>Ropa</strong> y el stand es mayor a <strong>$1,000</strong>. Luego ordena por color.',
            solution: [
              "En la hoja <strong>Expositores</strong>, seleccione el rango <strong>A3:A38</strong>.",
              "En <strong>Inicio → Estilos → Formato condicional → Nueva regla…</strong>",
              "Seleccione <em>Utilice una fórmula que determine las celdas para aplicar formato</em>.",
              "Escriba: <code>=Y(B3=\"Ropa\",D3>1000)</code>",
              "Haga clic en <strong>Formato…</strong> → <strong>Relleno</strong>: Color amarillo, Estilo: Atenuado al 6.25 %.",
              "<strong>Aceptar</strong> dos veces.",
              "<strong>Ordene</strong> la columna por color de celda para que las filas amarillas queden al inicio.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Insertar fecha y hora actual",
            description:
              "En la hoja <strong>Horario de Sesiones</strong>, utilice una función para agregar la fecha y hora actuales a la celda <strong>G1</strong>.",
            solution: [
              "En la hoja <strong>Horario de Sesiones</strong>, haga clic en la celda <strong>G1</strong>.",
              "En la barra de fórmulas escriba <strong>=AHORA</strong> y pulse <strong>Tab</strong>.",
              "Presione <strong>Enter</strong> para aceptar la función. (Fórmula: =AHORA()).",
            ],
            finalFormula: "=AHORA()",
          },
          {
            id: 4,
            title: "Tarea 4: Quitar duplicados",
            description:
              "Elimina todos los <strong>Nombres del Expositor</strong> duplicados de la hoja <strong>Expositores</strong> sin borrar ningún otro registro.",
            solution: [
              "En la hoja <strong>Expositores</strong>, haga clic en cualquier celda de la tabla.",
              "En <strong>Datos → Herramientas de datos → Quitar duplicados</strong>.",
              "Haga clic en <strong>Anular selección</strong>.",
              "Marque únicamente <strong>Nombre del expositor</strong>.",
              "Haga clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Crear Gráfico dinámico filtrado",
            description:
              "En la hoja <strong>Horario diario</strong>, crea un <strong>Gráfico dinámico</strong> desde la tabla dinámica. Esquina superior izquierda en <strong>C5</strong>. Filtra: ventas de <strong>Marketing</strong> para el <strong>Jueves</strong>.",
            solution: [
              "En <strong>Horario diario</strong>, haga clic en la tabla dinámica.",
              "En <strong>Análisis de tabla dinámica → Herramientas → Gráfico dinámico</strong>.",
              "Acepte el gráfico <em>Columnas agrupadas</em> y haga clic en <strong>Aceptar</strong>.",
              "Filtre <strong>Día</strong>: desmarque todo, marque <strong>Jueves</strong>.",
              "Filtre <strong>Seguimiento</strong>: marque <strong>Ventas</strong> y <strong>Marketing</strong>.",
              "Arrastre el gráfico para que su esquina quede en la celda <strong>C5</strong>.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "practica2",
    title: "Práctica 2",
    subtitle: "Exportaciones y análisis financiero",
    fullTitle: "Práctica 2: Exportaciones, envíos y análisis financiero",
    description:
      "¡Bienvenido a la Práctica 2! Aquí trabajarás con datos reales de ventas internacionales, envíos y análisis financiero para consolidar tus habilidades en Excel.",
    requirements: [
      "<strong>Microsoft 365</strong> o <strong>Excel 2024</strong> (Windows, Mac o Excel en la web).",
      "Conocimientos de funciones, tablas dinámicas y gráficos.",
      'Complemento <strong>"Modo Imparable"</strong> instalado y activo.',
    ],
    projects: [
      /* ═══════════════════════  PROYECTO 1 (P2)  ═══════════════════════ */
      {
        id: "project6",
        title: "Proyecto 1: Comercio Internacional",
        subtitle: "Ventas internacionales y análisis de envíos",
        description:
          "Trabaja para una empresa de exportación de alimentos.\n\nEstás creando un libro de Excel para analizar las ventas internacionales, los tiempos de envío y los costos financieros de expansión.",
        topics: [
          "Subtotales",
          "Funciones de fecha (DIASEM)",
          "Función PAGO (PMT)",
          "Tablas dinámicas con agrupación",
          "Estilos de gráfico",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Insertar subtotales en Ventas",
            description:
              "Ordene la hoja de trabajo <strong>Ventas</strong> por <strong>Nombre de producto</strong> de la A a la Z e inserte un <strong>Subtotal</strong> que calcule la <strong>Suma</strong> en la columna <strong>Precio extendido</strong> <strong>en cada cambio en el Nombre del producto</strong>.",
            solution: [
              "En la hoja <strong>Ventas</strong>, selecciona cualquier celda del rango de datos.",
              "En la pestaña <strong>Datos</strong>, grupo <strong>Ordenar y filtrar</strong>, haz clic en <strong>Ordenar</strong>.",
              "Ordena por <strong>Nombre del producto</strong> de <strong>A a Z</strong>. Haz clic en <strong>Aceptar</strong>.",
              "Selecciona todo el rango de datos.",
              "En la pestaña <strong>Datos</strong>, grupo <strong>Esquema</strong>, haz clic en <strong>Subtotal</strong>.",
              "Configura: <strong>Para cada cambio en:</strong> <span class=\"formula\">Nombre del producto</span>, <strong>Usar función:</strong> <span class=\"formula\">Suma</span>, <strong>Agregar subtotal a:</strong> <span class=\"formula\">Precio extendido</span>.",
              "Haz clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Calcular día de la semana de envío",
            description:
              "En la celda <strong>H2</strong> de la hoja de trabajo <strong>Envíos</strong>, cree una fórmula que identifique el día numérico de la semana en que se envió el pedido usando <strong>Lunes = 0</strong> y <strong>Domingo = 6</strong>. Aplique esa fórmula a toda la columna H.",
            solution: [
              "En la hoja <strong>Envíos</strong>, haz clic en la celda <strong>H2</strong>.",
              "En la barra de fórmulas escribe <span class=\"formula\">=DIASEM(G2,3)</span> y presiona <strong>Enter</strong>.",
              "El argumento <strong>3</strong> indica que Lunes = 0 y Domingo = 6.",
              "Selecciona la celda <strong>H2</strong> y arrastra el <strong>Controlador de relleno</strong> hasta la última fila con datos.",
            ],
            finalFormula: "=DIASEM(G2,3)",
          },
          {
            id: 3,
            title: "Tarea 3: Calcular la mensualidad del préstamo",
            description:
              "Para almacenar nuevos artículos especiales, su empresa de exportación construyó una extensión en su almacén.<br/><br/>En la celda <strong>B7</strong> de la hoja de trabajo <strong>Extensión de almacen</strong>, agregue una fórmula que calcule el monto del pago mensual, suponiendo que el pago vence a principios de mes.",
            solution: [
              "En la hoja <strong>Extensión de almacen</strong>, haz clic en la celda <strong>B7</strong>.",
              "Escribe <span class=\"formula\">=PAGO(B5/12,B6*12,B4,0,1)</span> y presiona <strong>Enter</strong>.",
              "El resultado debe ser aproximadamente <strong>-$2,830.48</strong> (negativo porque es un pago).",
            ],
            expectedResult: "-$2,830.48",
            finalFormula: "=PAGO(B5/12,B6*12,B4,0,1)",
          },
          {
            id: 4,
            title: "Tarea 4: Agrupar productos por país",
            description:
              "En la hoja de trabajo <strong>Resumen gráfico</strong>, modifique la tabla de modo que los <strong>Nombres de Productos</strong> se agrupen dentro de cada <strong>País</strong>.",
            solution: [
              "En la hoja <strong>Ventas</strong>, selecciona cualquier celda del rango de datos.",
              "En <strong>Insertar → Tabla dinámica</strong>.",
              "En <strong>Hoja de cálculo existente</strong>, selecciona una celda de la hoja <strong>Resumen gráfico</strong>.",
              "Haz clic en <strong>Aceptar</strong>.",
              "Arrastra <strong>País</strong> al área <strong>Filas</strong>.",
              "Arrastra <strong>Nombre del producto</strong> al área <strong>Filas</strong> (debajo de País).",
              "Arrastra <strong>Precio extendido</strong> al área <strong>Valores</strong>.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Aplicar estilo al gráfico",
            description:
              "En la hoja <strong>Resumen Gráfico</strong>, aplique <strong>Estilo 4</strong> al Gráfico dinámico.",
            solution: [
              "Haz clic en la tabla dinámica de la hoja <strong>Resumen gráfico</strong>.",
              "En <strong>Análisis de tabla dinámica → Herramientas → Gráfico dinámico</strong>.",
              "Selecciona <strong>Barras agrupadas</strong> y haz clic en <strong>Aceptar</strong>.",
              "Con el gráfico seleccionado, ve a la pestaña <strong>Diseño de gráfico</strong>.",
              "En el grupo <strong>Estilos de diseño</strong>, selecciona el <strong>Estilo 4</strong>.",
            ],
          },
        ],
      },
      /* ═══════════════════════  PROYECTO 2 (P2)  ═══════════════════════ */
      {
        id: "project7",
        title: "Proyecto 2: Ruta Gastronómica",
        subtitle: "Ventas de camiones de comida y análisis de inventario",
        description:
          "Usted y un socio poseen una pequeña flota de Comida Móvil.\n\nEstá creando un libro de trabajo para rastrear su inventario y analizar sus ingresos.",
        topics: [
          "Formato condicional para duplicados",
          "Funciones CONTAR.SI.CONJUNTO",
          "Gráfico de proyección solar",
          "Función AHORA (NOW)",
          "Totales generales en tablas dinámicas",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Resaltar alimentos duplicados",
            description:
              "En la hoja de trabajo <strong>Inventario de alimentos</strong>, escriba una regla de formato condicional para el rango <strong>A2:A57</strong> que establezca el color de la fuente en <strong>rojo</strong> para todos los valores duplicados en la columna.",
            solution: [
              "En la hoja <strong>Inventario de alimentos</strong>, selecciona el rango <strong>A2:A57</strong>.",
              "En la pestaña <strong>Inicio</strong>, haz clic en <strong>Formato condicional</strong>.",
              "Elige <strong>Resaltar reglas de celdas → Valores duplicados…</strong>",
              "En el formato, configura la <strong>fuente en color rojo</strong>.",
              "Haz clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Contar camiones con ventas altas de hamburguesas",
            description:
              "En la celda <strong>H12</strong> en la hoja de cálculo <strong>Ventas actuales T1</strong>, utilice una fórmula para calcular el número de camiones que tuvieron las ventas de Hamburguesas de más de <strong>$2,500</strong> en <strong>marzo</strong>.",
            solution: [
              "En la hoja <strong>Ventas actuales T1</strong>, haz clic en la celda <strong>H12</strong>.",
              "Escribe <span class=\"formula\">=CONTAR.SI.CONJUNTO</span> y presiona <strong>Tab</strong>.",
              "Haz clic en <strong>fx</strong>. Ingrese: <strong>Rango_criterios1:</strong> B2:B35, <strong>Criterio1:</strong> \"Hamburguesa\", <strong>Rango_criterios2:</strong> E2:E35, <strong>Criterio2:</strong> \">2500\".",
              "Haz clic en <strong>Aceptar</strong>. (Resultado: 2).",
            ],
            expectedResult: "2",
            finalFormula: "=CONTAR.SI.CONJUNTO(B2:B35,\"Hamburguesa\",E2:E35,\">2500\")",
          },
          {
            id: 3,
            title: "Tarea 3: Crear gráfico de proyección solar",
            description:
              "En la hoja de trabajo <strong>Objetivo de ventas T1</strong>, cree un gráfico <strong>Proyección solar</strong> utilizando solo los datos de las columnas <strong>Ubicación</strong> y <strong>Totales</strong>. Coloque el gráfico a la derecha de la tabla.",
            solution: [
              "En la hoja <strong>Objetivo de ventas T1</strong>, selecciona la columna <strong>Ubicación</strong> (A1:A6). Mantén <strong>Ctrl</strong> y selecciona la columna <strong>Total</strong> (E1:E6).",
              "En la pestaña <strong>Insertar</strong>, grupo <strong>Gráficos</strong>, haz clic en <strong>Insertar gráfico de jerarquía</strong>.",
              "Selecciona <strong>Proyección solar (Sunburst)</strong>.",
              "Arrastra el gráfico a la derecha de la tabla.",
            ],
          },
          {
            id: 4,
            title: "Tarea 4: Agregar reloj con fecha y hora actual",
            description:
              "En la hoja de trabajo <strong>Ventas actuales T1</strong>, agregue un reloj a la celda <strong>H13</strong>.",
            solution: [
              "En la hoja <strong>Ventas actuales T1</strong>, haz clic en la celda <strong>H13</strong>.",
              "En la barra de fórmulas escribe <strong>=AHORA</strong> y presiona <strong>Tab</strong>.",
              "Presiona <strong>Enter</strong> para aceptar la función. (Fórmula: =AHORA()).",
            ],
            finalFormula: "=AHORA()",
          },
          {
            id: 5,
            title: "Tarea 5: Totales generales solo en columnas",
            description:
              "En la hoja de cálculo <strong>Tabla dinámica</strong>, agregue <strong>Totales Generales</strong> solo a las columnas.",
            solution: [
              "En la hoja <strong>Tabla dinámica</strong>, haz clic en cualquier celda de la tabla dinámica.",
              "En la pestaña <strong>Diseño</strong>, grupo <strong>Diseño</strong>, haz clic en <strong>Totales generales</strong>.",
              "Selecciona <strong>Activado solo para columnas</strong>.",
            ],
          },
        ],
      },
      /* ═══════════════════════  PROYECTO 3 (P2)  ═══════════════════════ */
      {
        id: "project8",
        title: "Proyecto 3: Consultoría Global",
        subtitle: "Análisis de ciudades capitales del mundo",
        description:
          "Trabaja para una consultora internacional.\n\nEstá preparando un libro de trabajo para ayudar a analizar la información utilizada en su negocio.",
        topics: [
          "Quitar duplicados",
          "Cálculo manual",
          "Función INDICE + COINCIDIR",
          "Segmentación de datos",
          "Ordenar gráfico dinámico",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Eliminar registros duplicados",
            description:
              "Elimine todos los registros duplicados de la hoja de trabajo de <strong>Ciudades capitales mundiales</strong>. No elimine ningún otro registro.",
            solution: [
              "En la hoja <strong>Ciudades capitales del mundo</strong>, haz clic en cualquier celda de la tabla.",
              "En <strong>Datos → Herramientas de datos → Quitar duplicados</strong>.",
              "Asegúrate de que todas las columnas estén seleccionadas.",
              "Haz clic en <strong>Aceptar</strong>. (Se eliminan 2 registros duplicados).",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Configurar cálculo manual",
            description:
              "Configure las opciones del libro de cálculo para que las fórmulas no muestren cambios de resultados hasta que se vuelva a <strong>calcular de forma manual o hasta que se guarde el libro</strong>.",
            solution: [
              "Haz clic en <strong>Archivo → Opciones</strong>.",
              "En <strong>Fórmulas</strong>, busca <strong>Modo de cálculo de libro</strong>.",
              "Selecciona <strong>Manual</strong>.",
              "Activa <strong>Recalcular antes de guardar</strong>.",
              "Haz clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Fórmula INDICE + COINCIDIR",
            description:
              "En la celda <strong>G2</strong> en la hoja <strong>Ciudades capitales del mundo</strong>, utilice la función <strong>INDICE</strong> combinado con la función <strong>COINCIDIR</strong> para encontrar la población de la ciudad capital ingresada en la celda <strong>F2</strong>.",
            solution: [
              "En la hoja <strong>Ciudades capitales del mundo</strong>, haz clic en la celda <strong>G2</strong>.",
              "Escribe <span class=\"formula\">=INDICE(C:C,COINCIDIR(F2,B:B,0))</span>.",
              "Presiona <strong>Enter</strong>. (Resultado para Kabul: 2,206,300).",
            ],
            expectedResult: "2,206,300",
            finalFormula: "=INDICE(C:C,COINCIDIR(F2,B:B,0))",
          },
          {
            id: 4,
            title: "Tarea 4: Agregar Segmentación de datos",
            description:
              "En la hoja de trabajo <strong>Top 15 Ciudades</strong>, agregue una <strong>Segmentación de datos</strong> para la columna <strong>País</strong>. Coloque la segmentación a la derecha del Gráfico dinámico.",
            solution: [
              "En la hoja <strong>Top 15 Ciudades</strong>, haz clic en la tabla dinámica.",
              "En <strong>Análisis de tabla dinámica → Filtrar → Insertar Segmentación de datos</strong>.",
              "Marca la casilla <strong>País</strong> y haz clic en <strong>Aceptar</strong>.",
              "Arrastra la segmentación a la derecha del gráfico dinámico.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Ordenar gráfico por clasificación",
            description:
              "En la hoja de trabajo <strong>Top 15 Ciudades</strong>, modifique la tabla dinámica para que el campo <strong>Clasificación</strong> esté <strong>por encima</strong> de Ciudad Capital en el área de filas, de modo que el gráfico muestre las ciudades en <strong>orden de clasificación</strong> (1, 2, 3…) en lugar de alfabéticamente.",
            solution: [
              "Haz clic en la tabla dinámica en la hoja <strong>Top 15 Ciudades</strong>.",
              "En la lista de campos, arrastra <strong>Clasificación</strong> al área de <strong>Filas</strong>, posicionándolo <strong>arriba</strong> de Ciudad Capital.",
              "Verifica que el gráfico ahora muestra las ciudades ordenadas por clasificación (Tokyo primero).",
            ],
          },
        ],
      },
      /* ═══════════════════════  PROYECTO 4 (P2)  ═══════════════════════ */
      {
        id: "project9",
        title: "Proyecto 4: Dulcería Artesanal",
        subtitle: "Tienda de caramelos con sabores personalizados",
        description:
          "Una tienda que administra vende caramelos con sabores personalizados.\n\nEstá revisando el costo y las ganancias de cada sabor y resumiendo la información en un libro de trabajo.",
        topics: [
          "Buscar y reemplazar",
          "Títulos de impresión",
          "Formato de número",
          "Ejes de gráfico",
          "Leyenda de gráfico",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Buscar y reemplazar texto",
            description:
              "Simultáneamente, reemplace todas las apariciones de la palabra <strong>Choco</strong> con <strong>Chocolate</strong> en el libro de trabajo.",
            solution: [
              "Presiona <strong>Ctrl + H</strong> para abrir Buscar y reemplazar.",
              "En <strong>Buscar:</strong> escribe <span class=\"formula\">Choco</span>.",
              "En <strong>Reemplazar con:</strong> escribe <span class=\"formula\">Chocolate</span>.",
              "Haz clic en <strong>Reemplazar todos</strong>.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Configurar títulos de impresión",
            description:
              "En la hoja de trabajo <strong>Costos</strong>, repita las filas que contienen el logotipo de la empresa y los encabezados de columna para que aparezcan en <strong>todas las páginas impresas</strong>.",
            solution: [
              "En la hoja <strong>Costos</strong>, ve a <strong>Disposición de página → Imprimir títulos</strong>.",
              "En <strong>Filas a repetir en extremo superior:</strong> selecciona <span class=\"formula\">$1:$3</span>.",
              "Haz clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Aplicar formato de Número",
            description:
              "Aplique simultáneamente el formato de <strong>Número</strong> a las columnas de <strong>B a E</strong> en la hoja de cálculo de <strong>Costos</strong>.",
            solution: [
              "En la hoja <strong>Costos</strong>, selecciona las columnas <strong>B:E</strong> (datos numéricos).",
              "En la pestaña <strong>Inicio → Número</strong>, selecciona <strong>Número</strong> del menú desplegable.",
              "O presiona <strong>Ctrl + 1</strong> y selecciona la categoría <strong>Número</strong>.",
            ],
          },
          {
            id: 4,
            title: "Tarea 4: Modificar ejes del gráfico",
            description:
              "Modifique el gráfico en la hoja de trabajo <strong>Ganancias</strong> para que los <strong>Sabores</strong> sean mostrados como <strong>Etiquetas de eje horizontal</strong> y para que <strong>Gastos</strong> e <strong>Ingresos</strong> sean las <strong>leyendas de las series</strong>.",
            solution: [
              "Haz clic en el gráfico en la hoja <strong>Ganancias</strong>.",
              "En <strong>Diseño de gráfico → Datos → Cambiar fila/columna</strong>.",
              "Verifica que los sabores aparezcan en el eje horizontal y Gastos/Ingresos en la leyenda.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Mover leyenda del gráfico",
            description:
              "Modifique el gráfico de la hoja de trabajo <strong>Ganancias</strong> para que la <strong>Leyenda</strong> aparezca en la <strong>parte de Arriba</strong>.",
            solution: [
              "Haz clic en el gráfico en la hoja <strong>Ganancias</strong>.",
              "Haz clic en la leyenda del gráfico.",
              "En <strong>Formato → Posición de la leyenda</strong>, selecciona <strong>Superior</strong>.",
            ],
          },
        ],
      },
      /* ═══════════════════════  PROYECTO 5 (P2)  ═══════════════════════ */
      {
        id: "project10",
        title: "Proyecto 5: Academia de Buceo",
        subtitle: "Catalogo de cursos de buceo para resort",
        description:
          "Usted es el director del curso de un resort que ofrece una variedad de clases de buceo para turistas.\n\nEstá creando un libro de trabajo para ayudarlo a administrar los cursos.",
        topics: [
          "Proteger libro",
          "Agrupar filas",
          "BUSCARV / VLOOKUP",
          "Opciones de corrección",
          "Idioma de edición",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Proteger libro de trabajo",
            description:
              "Proteja el libro de trabajo para que los usuarios no puedan agregar, cambiar o eliminar hojas de trabajo. Utilice la contraseña <strong>12345</strong>.",
            solution: [
              "Ve a <strong>Revisar → Proteger libro</strong>.",
              "Marca la casilla <strong>Estructura</strong>.",
              "Escribe la contraseña <span class=\"formula\">12345</span>.",
              "Confirma la contraseña y haz clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Agrupar filas de Curso básico",
            description:
              "En la hoja de trabajo <strong>Cursos</strong>, debajo del encabezado <strong>Curso básico</strong>, agrupe las filas <strong>7 a 12</strong>.",
            solution: [
              "En la hoja <strong>Cursos</strong>, selecciona las filas <strong>7 a 12</strong>.",
              "Ve a <strong>Datos → Esquema → Agrupar</strong>.",
              "Selecciona <strong>Filas</strong> y haz clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Función de búsqueda de instructor",
            description:
              "En la hoja de trabajo <strong>Encontrar un instructor</strong>, inserte una función en la celda <strong>B8</strong> que muestre el nombre del instructor de SCUBA de la hoja de trabajo <strong>Cursos</strong> que está calificado para enseñar el curso que se muestra en la celda <strong>B6</strong>.",
            solution: [
              "En la hoja <strong>Encontrar un instructor</strong>, haz clic en la celda <strong>B8</strong>.",
              "Escribe <span class=\"formula\">=BUSCARV(B6,Cursos!A:B,2,0)</span>.",
              "Presiona <strong>Enter</strong>. (Resultado: Dan Webster).",
            ],
            expectedResult: "Dan Webster",
            finalFormula: "=BUSCARV(B6,Cursos!A:B,2,0)",
          },
          {
            id: 4,
            title: "Tarea 4: Opciones de corrección — Voseo",
            description:
              "Modifique las opciones de corrección para permitir <strong>Solo verbos de voseo en español</strong>.",
            solution: [
              "Haga clic en la pestaña <strong>Archivo</strong>. En la parte inferior del panel izquierdo, seleccione <strong>Opciones</strong>.",
              "En la ventana emergente <strong>Opciones de Excel</strong>, seleccione <strong>Revisión</strong> de la lista de la izquierda.",
              "Debajo del botón <strong>Diccionarios personalizados…</strong> haga clic en los <strong>Modos del español:</strong> flecha hacia abajo y seleccione <strong>Solo formas verbales de voseo</strong>.",
              "Haga clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Agregar idioma de edición",
            description:
              "Agregue <strong>Español (México)</strong> como idioma de edición, pero no configure el teclado.",
            solution: [
              "En la pestaña <strong>Archivo</strong>, haga clic en <strong>Opciones</strong>.",
              "En la ventana emergente <strong>Opciones de Excel</strong>, haga clic en <strong>Idioma</strong>.",
              "Debajo de <strong>Corrección e idiomas de creación de Office</strong>, haga clic en la flecha hacia abajo junto a <strong>[Agregar idioma de creación]</strong> y seleccione <strong>Español (México)</strong>.",
              "Haga clic en el botón <strong>Agregar</strong>.",
              "No habilite el teclado. Acepte todas las demás configuraciones predeterminadas.",
              "Haga clic en <strong>Aceptar</strong> para salir de la ventana.",
            ],
          },
        ],
      },
    ],
  },
  /* ═════════════════════════  PRÁCTICA 3  ═════════════════════════ */
  {
    id: "practica3",
    title: "Práctica 3",
    subtitle: "Gestión de datos y formato",
    fullTitle: "Práctica 3: Domina la gestión de datos en Excel",
    description:
      "¡Avanza al siguiente nivel! En estos proyectos trabajarás con importación de datos, formato de tablas, configuración de impresión y herramientas de compatibilidad.",
    requirements: [
      "<strong>Microsoft 365</strong> o <strong>Excel 2024</strong> (Windows, Mac o Excel en la web).",
      "Conocimientos intermedios: tablas, formato condicional y configuración de página.",
      'Complemento <strong>"Modo Imparable"</strong> instalado y activo.',
    ],
    projects: [
      /* ═══════════════════════  PROYECTO 1 (P3)  ═══════════════════════ */
      {
        id: "project11",
        title: "Proyecto 1: Equipo de Nieve",
        subtitle: "Tarifas de alquiler de equipo invernal",
        description:
          "Usted administra la tienda de alquiler en un gran complejo invernal de montaña.\n\nEs su responsabilidad actualizar la lista de tarifas de alquiler y mantener el libro de trabajo organizado.",
        topics: [
          "Importar CSV",
          "Borrar formatos",
          "Renombrar tabla",
          "Orientación de página",
          "Compatibilidad",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Importar archivo CSV",
            description:
              "Importe el archivo delimitado por comas <strong>Accesorios.csv</strong> en una nueva hoja de trabajo y acepte todos los demás valores predeterminados.",
            downloadableFiles: [
              {
                filename: "Accesorios.csv",
                content:
                  "101,Esquí de rendimiento único,30,20\n102,Tabla de snowboard,30,20\n103,Botas,8,5\n104,Postes,7,5\n105,Casco,7,5\n106,Raquetas de nieve,10,8\n107,Tobogán,40,30\n108,Trineo,20,15\n109,Tubo,8,5\n",
              },
            ],
            solution: [
              "Descargue el archivo <strong>Accesorios.csv</strong> usando el botón de descarga de arriba.",
              "Haga clic en la pestaña <strong>Datos</strong>.",
              "En el grupo <strong>Obtener y transformar datos</strong>, haga clic en <strong>Desde el texto/CSV</strong>.",
              "Busque y seleccione el archivo <strong>Accesorios.csv</strong> y haga clic en <strong>Importar</strong>.",
              "En la ventana emergente, asegúrese de que <strong>Delimitador</strong> esté configurado en <strong>Coma</strong>.",
              "En la parte inferior, haga clic en la flecha desplegable junto a <strong>Cargar</strong> y haga clic en <strong>Cargar en…</strong>",
              "En la ventana emergente <strong>Importar datos</strong>, asegúrese de que <strong>Hoja de cálculo nueva</strong> esté marcada.",
              "Haga clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Borrar formato de encabezados",
            description:
              "Borre el formato en el rango de celdas <strong>A4:D4</strong> en la hoja de trabajo <strong>Tarifa de alquiler</strong>.",
            solution: [
              "Seleccione el rango de celdas <strong>A4:D4</strong> en la hoja de trabajo <strong>Tarifa de alquiler</strong>.",
              "En la pestaña <strong>Inicio</strong>, en el grupo <strong>Edición</strong>, haga clic en <strong>Borrar</strong> y seleccione <strong>Borrar formatos</strong>.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Renombrar tabla",
            description:
              "Cambie el nombre de <strong>Tabla1</strong> a <strong>Tarifas</strong>.",
            solution: [
              "En la pestaña <strong>Fórmulas</strong>, en el grupo <strong>Nombres definidos</strong>, haga clic en <strong>Administrador de nombres</strong>.",
              "En la ventana emergente <strong>Administrador de nombres</strong>, asegúrese de que la <strong>Tabla1</strong> esté seleccionada y haga clic en el botón <strong>Modificar</strong>.",
              "En la ventana emergente <strong>Editar nombre</strong>, reemplace el nombre <strong>Tabla1</strong> con <span class=\"formula\">Tarifas</span>.",
              "Haga clic en <strong>Aceptar</strong>.",
              "Haga clic en <strong>Cerrar</strong>.",
            ],
          },
          {
            id: 4,
            title: "Tarea 4: Cambiar orientación de página",
            description:
              "Cambie la orientación de la hoja de trabajo <strong>Tarifa de alquiler</strong> a <strong>horizontal</strong>.",
            solution: [
              "Seleccione la hoja de trabajo <strong>Tarifa de alquiler</strong>.",
              "En la pestaña <strong>Disposición de página</strong>, grupo <strong>Configurar página</strong>, seleccione el menú desplegable <strong>Orientación</strong>.",
              "Seleccione <strong>Horizontal</strong>.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Comprobar compatibilidad",
            description:
              "Inspeccione el libro de trabajo por problemas de <strong>compatibilidad</strong>. Copie los resultados a una nueva hoja de trabajo.",
            solution: [
              "Haga clic en la pestaña <strong>Archivo</strong>.",
              "En la página de información predeterminada, haga clic en el botón <strong>Comprobar si hay problemas</strong>.",
              "Seleccione <strong>Comprobar compatibilidad</strong>.",
              "En la parte inferior de la ventana emergente <strong>Comprobador de compatibilidad de Microsoft Excel</strong>, haga clic en <strong>Copiar en hoja nueva</strong>.",
            ],
          },
        ],
      },
      /* ═══════════════════════  PROYECTO 2 (P3)  ═══════════════════════ */
      {
        id: "project12",
        title: "Proyecto 2: Tienda de Camping",
        subtitle: "Inventario de tienda de artículos para acampar",
        description:
          "Una tienda de artículos para acampar con descuento está creando un informe de inventario.\n\nNecesitan ayuda con gráficos, hipervínculos y formato condicional.",
        topics: [
          "Combinar celdas",
          "Hipervínculos internos",
          "Formato condicional con iconos",
          "Rango de datos de gráfico",
          "Estilos de gráfico",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Combinar celdas del título",
            description:
              "Combine las celdas <strong>A1:E1</strong> de la hoja de trabajo <strong>Pesca</strong>. No cambie la alineación de los contenidos.",
            solution: [
              "Seleccione el rango de celdas <strong>A1:E1</strong> en la hoja de trabajo <strong>Pesca</strong>.",
              "En la pestaña <strong>Inicio</strong>, en el grupo <strong>Alineación</strong>, haga clic en la flecha de la derecha de <strong>Combinar y centrar</strong> y seleccione <strong>Combinar celdas</strong>.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Crear hipervínculo interno",
            description:
              "En la hoja de trabajo <strong>Tiendas de campaña</strong>, vincule el contenido en la celda <strong>B13</strong> a la celda <strong>A4</strong> en la hoja de trabajo <strong>Pesca</strong>.",
            solution: [
              "Seleccione la celda <strong>B13</strong> en la hoja de trabajo <strong>Tiendas de campaña</strong>.",
              "En la pestaña <strong>Insertar</strong>, en el grupo <strong>Vínculos</strong>, haga clic en <strong>Insertar vínculo</strong>.",
              "En la sección <strong>Vincular a:</strong> seleccione <strong>Lugar de este documento</strong>.",
              "En el campo <strong>Escriba la referencia de celda</strong>, escriba <span class=\"formula\">A4</span>.",
              "En el campo <strong>O selecciona un lugar de este documento</strong>, en <strong>Referencia de celda</strong>, resalte <strong>Pesca</strong>.",
              "Haga clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Formato condicional con iconos",
            description:
              "En la hoja de trabajo <strong>Tiendas de campaña</strong>, aplique <strong>Formato condicional</strong> usando el <strong>Conjunto de iconos de 3 banderas</strong> a los valores en la columna <strong>Inventario</strong>.",
            solution: [
              "Seleccione el rango de celdas <strong>C4:C11</strong> en la hoja de trabajo <strong>Tiendas de campaña</strong>.",
              "En la pestaña <strong>Inicio</strong>, en el grupo <strong>Estilos</strong>, haga clic en <strong>Formato condicional</strong> para abrir el menú.",
              "Seleccione <strong>Conjuntos de iconos</strong> y haga clic en <strong>3 banderas</strong>.",
            ],
          },
          {
            id: 4,
            title: "Tarea 4: Expandir rango de datos del gráfico",
            description:
              "En la hoja de trabajo <strong>Tiendas de campaña</strong>, expanda el rango de datos del gráfico para incluir la <strong>Tienda gigante para cama de camión</strong> en la celda <strong>B7</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Tiendas de campaña</strong>, haga clic en el centro del gráfico para seleccionarlo.",
              "Haga clic en la pestaña <strong>Diseño de gráfico</strong>.",
              "En el grupo <strong>Datos</strong>, haga clic en <strong>Seleccionar datos</strong>.",
              "(Nota: La vista cambiará a la fuente de datos en la hoja de trabajo, rango de celdas B4:C6).",
              "Mantenga presionada la tecla <strong>CTRL</strong> y seleccione el rango de celdas <strong>B7:C7</strong>.",
              "Haga clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Aplicar estilo al gráfico",
            description:
              "Aplique el <strong>Estilo 4</strong> al gráfico ubicado en la hoja de trabajo <strong>Tiendas de campaña</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Tiendas de campaña</strong>, haga clic en el gráfico para seleccionarlo.",
              "Haga clic en la pestaña contextual <strong>Diseño de gráfico</strong>.",
              "En el grupo <strong>Estilos de diseño</strong>, haga clic en la flecha hacia abajo <strong>Más</strong> para abrir la galería <strong>Estilos de gráfico</strong>.",
              "Seleccione <strong>Estilo 4</strong>.",
            ],
          },
        ],
      },
      {
        id: "project13",
        title: "Proyecto 3: Infraestructura TI",
        subtitle: "Lista de precios del hardware de red",
        description:
          "Usted es un proveedor local que vende, instala y da soporte a equipos de redes inform\u00E1ticas.\n\nEst\u00E1s actualizando tu lista de precios.",
        topics: [
          "Rangos con nombre",
          "F\u00F3rmulas con nombres definidos",
          "Formato condicional",
          "Orientaci\u00F3n del texto",
          "Ordenaci\u00F3n multinivel",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Crear rango con nombre",
            description:
              "Cree un rango con nombre a partir de los rangos de celdas <strong>D5:D15</strong> y <strong>D18:D26</strong>. Nombre el rango <strong>Precios</strong>.",
            solution: [
              "Seleccione el rango de celdas <strong>D5:D15</strong>.",
              "Mientras mantiene presionada la tecla <strong>CTRL</strong>, seleccione tambi\u00E9n el rango <strong>D18:D26</strong>.",
              "Con ambos rangos seleccionados, vaya a la pesta\u00F1a <strong>F\u00F3rmulas</strong>, grupo <strong>Nombres definidos</strong>, y haga clic en <strong>Administrador de nombres</strong>.",
              "En la ventana emergente, haga clic en <strong>Nuevo</strong>.",
              "En <strong>Nombre</strong>, escriba <span class=\"formula\">Precios</span>.",
              "Verifique que <strong>Se refiere a</strong> contenga <span class=\"formula\">'Lista de precios'!$D$5:$D$15,'Lista de precios'!$D$18:$D$26</span>.",
              "Haga clic en <strong>Aceptar</strong> y luego en <strong>Cerrar</strong>.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Sumar enrutadores de 10 Gbps",
            description:
              "En la celda <strong>H5</strong>, ingrese una f\u00F3rmula que haga referencia a los rangos nombrados <strong>Precio_10G</strong>, <strong>Instalaci\u00F3n_10G</strong> y <strong>Soporte_10G</strong> para mostrar el valor de la suma total de los <strong>enrutadores de 10 Gbps</strong>.",
            solution: [
              "Haga clic en la celda <strong>H5</strong>.",
              "En la barra de f\u00F3rmulas, escriba <span class=\"formula\">=SUMA(Precio_10G, Instalaci\u00F3n_10G, Soporte_10G)</span>.",
              "Presione <strong>Enter</strong> para confirmar la f\u00F3rmula.",
            ],
            finalFormula:
              "=SUMA(Precio_10G, Instalaci\u00F3n_10G, Soporte_10G)",
          },
          {
            id: 3,
            title: "Tarea 3: Eliminar formato condicional",
            description:
              "Elimine <strong>todo el formato condicional</strong> en la hoja de trabajo.",
            solution: [
              "En la pesta\u00F1a <strong>Inicio</strong>, grupo <strong>Estilos</strong>, haga clic en <strong>Formato condicional</strong>.",
              "Seleccione <strong>Borrar reglas</strong> y luego <strong>Borrar reglas de toda la hoja</strong>.",
            ],
          },
          {
            id: 4,
            title: "Tarea 4: Girar encabezados de ambas tablas",
            description:
              "En la hoja de trabajo <strong>Lista de precios</strong>, gire simult\u00E1neamente el texto <strong>Tama\u00F1o del puerto</strong>, <strong>Tama\u00F1o de banda</strong>, <strong>Precio</strong>, <strong>Instalaci\u00F3n</strong> y <strong>Soporte</strong> en ambas tablas para tener un <strong>\u00C1ngulo ascendente</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Lista de precios</strong>, seleccione el rango <strong>C4:F4</strong> y, manteniendo presionada la tecla <strong>CTRL</strong>, seleccione tambi\u00E9n <strong>C17:F17</strong>.",
              "En la pesta\u00F1a <strong>Inicio</strong>, grupo <strong>Alineaci\u00F3n</strong>, haga clic en <strong>Orientaci\u00F3n</strong> y seleccione <strong>\u00C1ngulo ascendente</strong>.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Clasificaci\u00F3n de m\u00FAltiples columnas",
            description:
              "Realice una clasificaci\u00F3n de m\u00FAltiples columnas en los datos de la tabla de <strong>Equipos al\u00E1mbricos</strong>. Primero ordene alfab\u00E9ticamente por <strong>Equipo al\u00E1mbrico</strong> (de la A a la Z), luego ordene por <strong>Tama\u00F1o del puerto</strong> (de la A a la Z).",
            solution: [
              "Haga clic en cualquier celda de la tabla <strong>Equipos al\u00E1mbricos</strong>.",
              "En la pesta\u00F1a <strong>Inicio</strong>, grupo <strong>Edici\u00F3n</strong>, haga clic en <strong>Ordenar y filtrar</strong> y seleccione <strong>Orden personalizado...</strong>.",
              "Configure el primer nivel como <strong>Ordenar por:</strong> Equipo al\u00E1mbrico, <strong>Ordenar seg\u00FAn:</strong> Valores de celda, <strong>Orden:</strong> A a Z.",
              "Haga clic en <strong>Agregar nivel</strong>.",
              "Configure el segundo nivel como <strong>Luego por:</strong> Tama\u00F1o del puerto, <strong>Ordenar seg\u00FAn:</strong> Valores de celda, <strong>Orden:</strong> A a Z.",
              "Haga clic en <strong>Aceptar</strong>.",
            ],
          },
        ],
      },
      {
        id: "project14",
        title: "Proyecto 4: Catálogo de Bicicletas",
        subtitle: "Catálogo y ventas del primer trimestre",
        description:
          "Usted es un contratista para el ejército y responsable de vender su inventario de bicicletas excedentes.\n\nEstá modificando un libro de trabajo para informar ventas para el primer trimestre.",
        topics: [
          "Inmovilizar paneles",
          "Propiedades del documento",
          "Área de impresión",
          "SUMA",
          "CONCATENAR",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Inmovilizar filas 1 a 3",
            description:
              "Configure la hoja de trabajo <strong>Catálogo</strong> para que las filas <strong>1 a 3</strong> permanezcan visibles mientras se desplaza verticalmente.",
            solution: [
              "En la hoja de trabajo <strong>Catálogo</strong>, seleccione la fila <strong>4</strong>.",
              "En la pestaña <strong>Vista</strong>, grupo <strong>Ventana</strong>, haga clic en <strong>Inmovilizar</strong> y seleccione <strong>Inmovilizar paneles</strong>.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Estado del documento",
            description:
              "Agregue el texto <strong>Borrador</strong> a la propiedad <strong>Estado</strong> en las propiedades del documento.",
            solution: [
              "Haga clic en la pestaña <strong>Archivo</strong>.",
              "En la página de <strong>Información</strong>, haga clic en <strong>Mostrar todas las propiedades</strong>.",
              "En el campo <strong>Estado</strong>, escriba <span class=\"formula\">Borrador</span>.",
              "Regrese al documento.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Establecer área de impresión",
            description:
              "Configure Excel para imprimir siempre el rango de celdas <strong>A1:F17</strong> en la hoja de trabajo <strong>Ventas T1</strong>.",
            solution: [
              "En la hoja <strong>Ventas T1</strong>, seleccione el rango <strong>A1:F17</strong>.",
              "Haga clic en la pestaña <strong>Disposición de página</strong>.",
              "En <strong>Configurar página</strong>, haga clic en <strong>Área de impresión</strong> y seleccione <strong>Establecer área de impresión</strong>.",
            ],
          },
          {
            id: 4,
            title: "Tarea 4: Calcular ventas totales",
            description:
              "En la hoja de trabajo <strong>Ventas T1</strong>, inserte una función en la celda <strong>B19</strong> que calcule todas las ventas de la columna <strong>Total</strong>.",
            solution: [
              "En la hoja <strong>Ventas T1</strong>, seleccione la celda <strong>B19</strong>.",
              "Escriba <span class=\"formula\">=SUMA(F4:F17)</span>.",
              "Presione <strong>Enter</strong>. El resultado debe ser <span class=\"formula\">$ 251,422.00</span>.",
            ],
            expectedResult: "$ 251,422.00",
            finalFormula: "=SUMA(F4:F17)",
          },
          {
            id: 5,
            title: "Tarea 5: Combinar descripción y estilo",
            description:
              "En la celda <strong>B4</strong> de la hoja de trabajo <strong>Ventas T1</strong>, inserte una función que combine la <strong>Descripción</strong> y el <strong>Estilo</strong> ubicados en la hoja de trabajo <strong>Catálogo</strong>. Separe la Descripción y el Estilo con un guión y un espacio en ambos lados del guión.",
            solution: [
              "En la hoja <strong>Ventas T1</strong>, seleccione la celda <strong>B4</strong>.",
              "Inserte la función <strong>CONCATENAR</strong>.",
              "Use como argumentos: <span class=\"formula\">Catálogo!D4</span>, <span class=\"formula\">\" - \"</span> y <span class=\"formula\">Catálogo!C4</span>.",
              "Haga clic en <strong>Aceptar</strong>.",
            ],
            expectedResult: "Campo traviesa - Rígida",
            finalFormula: '=CONCATENAR(Catálogo!D4," - ",Catálogo!C4)',
          },
        ],
      },
      {
        id: "project15",
        title: "Proyecto 5: Plataforma de Contenido",
        subtitle: "Entradas publicadas por trimestre",
        description:
          "Usted es due\u00F1o de un blog de recetas en l\u00EDnea. Est\u00E1 creando un libro de trabajo para registrar las entradas publicadas en su blog.",
        topics: [
          "Fila de totales",
          "MAX",
          "Gr\u00E1fico circular 2D",
          "Dar formato como tabla",
          "Dise\u00F1o r\u00E1pido de gr\u00E1fico",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Agregar fila de totales",
            description:
              "En la hoja de trabajo <strong>Trimestre 1</strong>, agregue una fila a la tabla que calcule autom\u00E1ticamente las <strong>Entradas totales</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Trimestre 1</strong>, haga clic en cualquier celda de la tabla para seleccionarla.",
              "En la pesta\u00F1a <strong>Dise\u00F1o de tabla</strong>, en el grupo <strong>Opciones de estilo de tabla</strong>, habilite <strong>Fila de totales</strong>.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Calcular el máximo trimestral",
            description:
              "En la hoja de trabajo <strong>Trimestre 1</strong>, en la fila <strong>M\u00E1ximo</strong>, inserte una f\u00F3rmula en la columna <strong>E</strong> que devuelva el mayor n\u00FAmero de entradas de recetas para el trimestre.",
            solution: [
              "Seleccione la celda <strong>E12</strong> en la hoja de trabajo <strong>Trimestre 1</strong>.",
              "Inserte la funci\u00F3n <span class=\"formula\">=MAX(E3:E10)</span>.",
              "Presione <strong>Enter</strong>. El resultado debe ser <span class=\"formula\">80</span>.",
            ],
            expectedResult: "80",
            finalFormula: "=MAX(E3:E10)",
          },
          {
            id: 3,
            title: "Tarea 3: Crear gráfico circular 2D",
            description:
              "En la hoja de trabajo <strong>Trimestre 1</strong>, use los datos contenidos solo en las columnas <strong>Entradas</strong> y <strong>Total</strong> para crear un <strong>gr\u00E1fico Circular 2D</strong>. Coloque el nuevo gr\u00E1fico debajo de la tabla.",
            solution: [
              "Seleccione el rango <strong>A2:A10</strong> en la hoja de trabajo <strong>Trimestre 1</strong>.",
              "Mientras mantiene presionada la tecla <strong>CTRL</strong>, seleccione tambi\u00E9n <strong>E2:E10</strong>.",
              "En la pesta\u00F1a <strong>Insertar</strong>, en el grupo <strong>Gr\u00E1ficos</strong>, haga clic en <strong>Insertar gr\u00E1fico circular o de anillos</strong> y elija <strong>Circular 2D</strong>.",
              "Arrastre el nuevo gr\u00E1fico para colocarlo debajo de la tabla.",
            ],
          },
          {
            id: 4,
            title: "Tarea 4: Convertir rango en tabla",
            description:
              "En la hoja de c\u00E1lculo <strong>Trimestre 2</strong>, convierta el rango de celdas <strong>A2:E10</strong> en una tabla con encabezados y formato <strong>Estilo de tabla claro 14</strong>.",
            solution: [
              "Seleccione el rango <strong>A2:E10</strong> en la hoja de trabajo <strong>Trimestre 2</strong>.",
              "En la pesta\u00F1a <strong>Inicio</strong>, haga clic en <strong>Dar formato como tabla</strong>.",
              "En la secci\u00F3n <strong>Claro</strong>, seleccione el <strong>Estilo de tabla claro 14</strong>.",
              "Confirme que la tabla tiene encabezados y haga clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Aplicar diseño rápido 2",
            description:
              "Aplique el <strong>Dise\u00F1o 2</strong> al gr\u00E1fico de barras en la hoja de trabajo <strong>Trimestre 1</strong>.",
            solution: [
              "Seleccione el gr\u00E1fico de barras en la hoja <strong>Trimestre 1</strong>.",
              "En la pesta\u00F1a <strong>Dise\u00F1o de gr\u00E1fico</strong>, en el grupo <strong>Dise\u00F1os de gr\u00E1fico</strong>, haga clic en <strong>Dise\u00F1o r\u00E1pido</strong>.",
              "Seleccione <strong>Dise\u00F1o 2</strong>.",
            ],
          },
        ],
      },
      {
        id: "project16",
        title: "Proyecto 6: Tienda de Tecnología",
        subtitle: "Ventas trimestrales y bonos por empleado",
        description:
          "Usted es el asistente del gerente en una tienda que vende computadoras y soporte. La tienda ofrece bonos a los empleados que cumplen con los objetivos de ventas trimestrales. Se le ha encargado preparar las tablas que rastrean las ventas y los bonos.",
        topics: [
          "Referencias mixtas",
          "Autocompletar",
          "Eliminar filas",
          "Estilos de tabla",
          "Minigr\u00E1ficos",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Insertar referencia mixta",
            description:
              "En la hoja de trabajo <strong>Bonos de empleados</strong>, inserte una referencia mixta en la celda <strong>F4</strong> para que la f\u00F3rmula calcule correctamente la comisi\u00F3n cuando se copie de <strong>F4</strong> a <strong>F11</strong>. Copie la referencia en la columna para verificar que se calcule correctamente.",
            solution: [
              "Seleccione la celda <strong>F4</strong> en la hoja de trabajo <strong>Bonos de empleados</strong>.",
              "Escriba la f\u00F3rmula <span class=\"formula\">=B$15*E4</span>.",
              "Use el controlador de relleno para copiar la f\u00F3rmula hasta <strong>F11</strong>.",
            ],
            finalFormula: "=B$15*E4",
          },
          {
            id: 2,
            title: "Tarea 2: Copiar fórmula de compensación total",
            description:
              "En la hoja de trabajo <strong>Bonos de empleados</strong>, use <strong>Autocompletar</strong> para copiar la f\u00F3rmula en la celda <strong>G4</strong> y calcular la <strong>Compensaci\u00F3n total</strong> para cada empleado.",
            solution: [
              "Seleccione la celda <strong>G4</strong>.",
              "Arrastre el controlador de relleno hacia abajo hasta la celda <strong>G11</strong>.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Eliminar fila de Allen",
            description:
              "En la hoja de trabajo <strong>Partes</strong>, elimine la fila que contiene el vendedor llamado <strong>Allen</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Partes</strong>, ubique la fila del vendedor <strong>Allen</strong>.",
              "Haga clic con el bot\u00F3n derecho en la fila y seleccione <strong>Eliminar</strong>.",
            ],
          },
          {
            id: 4,
            title: "Tarea 4: Cambiar estilo de la tabla",
            description:
              "En la hoja de trabajo <strong>Partes</strong>, cambie el estilo de la tabla a <strong>Estilo de tabla medio 1</strong>.",
            solution: [
              "Seleccione cualquier celda dentro de la tabla en la hoja <strong>Partes</strong>.",
              "Abra la galer\u00EDa de <strong>Estilos de tabla</strong>.",
              "En la secci\u00F3n <strong>Medio</strong>, seleccione el <strong>Estilo de tabla medio 1</strong>.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Insertar minigráfico de línea",
            description:
              "En la hoja de trabajo <strong>Partes</strong>, inserte un <strong>Minigr\u00E1fico</strong> de tipo <strong>L\u00EDnea</strong> en la celda <strong>F4</strong> que muestre la tendencia de ventas desde <strong>Enero</strong> hasta <strong>Marzo</strong>.",
            solution: [
              "Seleccione la celda <strong>F4</strong> en la hoja de trabajo <strong>Partes</strong>.",
              "En la pesta\u00F1a <strong>Insertar</strong>, en el grupo <strong>Minigr\u00E1ficos</strong>, seleccione <strong>L\u00EDnea</strong>.",
              "Use <strong>B4:D4</strong> como rango de datos y <strong>F4</strong> como ubicaci\u00F3n.",
              "Haga clic en <strong>Aceptar</strong>.",
            ],
          },
        ],
      },
    ],
  },
  {
    id: "practica4",
    title: "Pr\u00E1ctica 4",
    subtitle: "Libros, tablas, gr\u00E1ficos y vistas",
    fullTitle: "Pr\u00E1ctica 4: Domina Excel con escenarios de oficina avanzados",
    description:
      "En esta pr\u00E1ctica trabajar\u00E1s con administraci\u00F3n de libros, formatos de tabla, dise\u00F1os de gr\u00E1ficos, paneles y funciones b\u00E1sicas de productividad en Excel.",
    requirements: [
      "<strong>Microsoft 365</strong> o <strong>Excel 2024</strong> (Windows, Mac o Excel en la web).",
      "Conocimientos previos de tablas, f\u00F3rmulas b\u00E1sicas y manejo general del libro.",
      'Complemento <strong>"Modo Imparable"</strong> instalado y activo.',
    ],
    projects: [
      {
        id: "project17",
        title: "Proyecto 1: Apps Móviles",
        subtitle: "Descargas populares de Android e iOS",
        description:
          "Trabajas para una empresa de estudios y desarrollo. Est\u00E1 compilando un informe sobre las 20 aplicaciones m\u00E1s populares para tel\u00E9fonos Android.",
        topics: [
          "Rangos con nombre",
          "Altura de fila",
          "Estilos de celda",
          "Dar formato como tabla",
          "Pie de p\u00E1gina",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Limpiar el rango nombrado EncabezadoApp2",
            description:
              "Navegue hasta el rango de celdas nombrado <strong>EncabezadoApp2</strong> y elimine solo el contenido, dejando las celdas vac\u00EDas.",
            solution: [
              "Use el cuadro de nombres o la opci\u00F3n <strong>Ir a</strong> para seleccionar <strong>EncabezadoApp2</strong>.",
              "Borre solo el contenido del rango seleccionado.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Ajustar altura de la fila 27",
            description:
              "En la hoja <strong>Descargas</strong>, ajuste la altura de la fila <strong>27</strong> a <strong>78</strong>.",
            solution: [
              "Seleccione la fila <strong>27</strong>.",
              "En la pestaña <strong>Inicio</strong>, en la sección <strong>Celdas</strong>, haga clic en <strong>Formato &gt; Alto de fila</strong> y escriba <strong>78</strong>.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Aplicar estilo de celda en A27",
            description:
              "Aplique el estilo de celda <strong>40% - \u00C9nfasis 2</strong> a la celda <strong>A27</strong>.",
            solution: [
              "Seleccione <strong>A27</strong>.",
              "En la pestaña <strong>Inicio</strong>, en la sección <strong>Estilos</strong>, haga clic en <strong>Estilos de celda</strong> y elija <strong>40% - \u00C9nfasis 2</strong>.",
            ],
          },
          {
            id: 4,
            title: "Tarea 4: Crear tabla en A3:B24",
            description:
              "Cree una tabla con encabezados del rango <strong>A3:B24</strong> aplicando <strong>Estilo de tabla claro 10</strong>.",
            solution: [
              "Seleccione el rango <strong>A3:B24</strong>.",
              "En la pestaña <strong>Inicio</strong>, haga clic en <strong>Dar formato como tabla</strong> y elija <strong>Estilo de tabla claro 10</strong>.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Pie de página con fecha actual",
            description:
              "Inserte un <strong>Pie de p\u00E1gina</strong> que muestre la fecha actual a la derecha y luego vuelva a la vista <strong>Normal</strong>.",
            solution: [
              "Vaya a la pestaña <strong>Insertar</strong> y haga clic en <strong>Encabezado y pie de página</strong>.",
              "Haga clic en la sección derecha del pie de página.",
              "En la pestaña <strong>Diseño de encabezado y pie de página</strong>, haga clic en <strong>Fecha actual</strong> para insertar el código <strong>&amp;[Fecha]</strong>.",
              "Regrese a la vista <strong>Normal</strong> desde la pestaña <strong>Vista</strong>.",
            ],
          },
        ],
      },
      {
        id: "project18",
        title: "Proyecto 2: Tienda de Mascotas",
        subtitle: "Inventario de alimentos para mascotas y ganado",
        description:
          "Usted es el asistente del gerente de una tienda de suministros para mascotas y ganado. Est\u00E1 creando un informe de inventario.",
        topics: [
          "Importar texto",
          "Hiperv\u00EDnculos",
          "Formato condicional",
          "Tablas",
          "Estilos de gr\u00E1fico",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Importar AlimentoMascotas.txt",
            description:
              "Importe el archivo <strong>AlimentoMascotas.txt</strong> como una tabla en una nueva hoja de trabajo.",
            solution: [
              "En la pestaña <strong>Datos</strong>, en el grupo <strong>Obtener y transformar datos</strong>, haga clic en <strong>Desde texto/CSV</strong>.",
              "Seleccione el archivo <strong>AlimentoMascotas.txt</strong> y haga clic en <strong>Importar</strong>.",
              "Configure: <strong>Origen de archivo:</strong> 1252: Europeo occidental (Windows), <strong>Delimitador:</strong> Tabulador.",
              "Haga clic en la flecha del botón <strong>Cargar</strong> y seleccione <strong>Cargar en...</strong>",
              "Seleccione <strong>Tabla</strong> y <strong>Hoja de cálculo nueva</strong>, luego haga clic en <strong>Aceptar</strong>.",
            ],
            downloadableFiles: [
              {
                filename: "AlimentoMascotas.txt",
                content:
                  "Artículo #\tDescripción\tInventario\t Precio \t Total \n100001\tAlimento para perro/ Carne\t5\t $26.99 \t $134.95 \n100002\tAlimento para perro/ Pollo\t4\t $26.99 \t $107.96 \n100003\tAlimento para gato/ Pollo\t6\t $26.99 \t $161.94 \n100004\tAlimento para gato/ Pescado\t2\t $26.99 \t $53.98 \n100005\tAlimento para gato sin gramos\t3\t $26.99 \t $80.97 \n100006\tAlimento para perrosin gramos\t5\t26.99\t134.95\n100007\tAlimento orgánico para conejos\t4\t22.99\t91.96\n100008\tAlimento para perros con/ Pavo\t2\t27.99\t55.98",
              },
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Quitar vínculo de C27",
            description:
              "En la hoja <strong>Inventario de alimentos</strong>, elimine la funcionalidad del v\u00EDnculo, pero deje el texto en la celda <strong>C27</strong>.",
            solution: [
              "En la hoja <strong>Inventario de alimentos</strong>, seleccione la celda <strong>C27</strong>.",
              "En la pestaña <strong>Insertar</strong>, en el grupo <strong>V\u00EDnculos</strong>, haga clic en <strong>Insertar v\u00EDnculo</strong>.",
              "En la ventana emergente, haga clic en <strong>Quitar v\u00EDnculo</strong>.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Borrar formato condicional del Inventario",
            description:
              "Elimine el formato condicional de la columna <strong>Inventario</strong> en la hoja de trabajo <strong>Inventario de alimentos</strong>.",
            solution: [
              "En la hoja <strong>Inventario de alimentos</strong>, haga clic en cualquier lugar de la tabla.",
              "En la pestaña <strong>Inicio</strong>, en el grupo <strong>Estilos</strong>, haga clic en <strong>Formato condicional</strong>.",
              "Seleccione <strong>Borrar reglas</strong> y haga clic en <strong>Borrar reglas de esta tabla</strong>.",
            ],
          },
          {
            id: 4,
            title: "Tarea 4: Tabla en Alimentos orgánicos",
            description:
              "En la hoja <strong>Alimentos org\u00E1nicos</strong>, formatee el rango de datos <strong>A3:F10</strong> como una tabla que tiene encabezados. Aplique el formato <strong>Estilo de tabla medio 7</strong>.",
            solution: [
              "En la hoja <strong>Alimentos org\u00E1nicos</strong>, seleccione el rango de celdas <strong>A3:F10</strong>.",
              "En la pestaña <strong>Inicio</strong>, en el grupo <strong>Estilos</strong>, haga clic en <strong>Dar formato como tabla</strong> para abrir la galer\u00EDa.",
              "En la secci\u00F3n <strong>Medio</strong>, haga clic en <strong>Estilo de tabla medio 7</strong>.",
              "En la ventana emergente <strong>Dar formato como tabla</strong>, confirme que el campo de datos contiene <strong>=$A$3:$F$10</strong>.",
              "Confirme que el cuadro <strong>La tabla tiene encabezados</strong> est\u00E1 habilitado.",
              "Haga clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Aplicar estilo 11 al gráfico",
            description:
              "En la hoja de trabajo <strong>Inventario de alimentos</strong>, aplique el <strong>Estilo 11</strong> al gr\u00E1fico.",
            solution: [
              "En la hoja <strong>Inventario de alimentos</strong>, haga clic en el gr\u00E1fico para seleccionarlo.",
              "Haga clic en la pestaña contextual <strong>Dise\u00F1o de gr\u00E1fico</strong>.",
              "En el grupo <strong>Estilos de dise\u00F1o</strong>, haga clic en la flecha hacia abajo <strong>M\u00E1s</strong> para abrir la galer\u00EDa <strong>Estilos de gr\u00E1fico</strong>.",
              "Seleccione el <strong>Estilo 11</strong>.",
            ],
          },
        ],
      },
      {
        id: "project19",
        title: "Proyecto 3: Repostería Gourmet",
        subtitle: "Costos, ingresos y ganancias por sabor",
        description:
          "Usted es un contador en una f\u00E1brica que produce masa de galletas con sabor gourmet. Est\u00E1 realizando un seguimiento de la cantidad vendida cada trimestre y resumiendo la informaci\u00F3n de un libro de trabajo.",
        topics: [
          "Saltos de p\u00E1gina",
          "Copiar formato",
          "PROMEDIO",
          "Seleccionar datos de gr\u00E1fico",
          "Filtros num\u00E9ricos",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Insertar salto de página",
            description:
              "Muestre la hoja de trabajo <strong>Costos</strong> en la vista <strong>Diseño de página</strong>. Luego inserte un salto de página entre la fila 20 <strong>Galleta Jacker</strong> y la fila 21 <strong>Chocolate con frambuesa</strong>.",
            solution: [
              "Seleccione la pestaña <strong>Vista</strong>.",
              "En el grupo <strong>Vistas de libro</strong>, haga clic en <strong>Diseño de página</strong>.",
              "Desplácese hacia abajo en la página y seleccione la fila 21 <strong>Chocolate con frambuesa</strong>.",
              "En la pestaña <strong>Disposición de página</strong>, en el grupo <strong>Configurar página</strong>, haga clic en <strong>Saltos</strong>.",
              "Seleccione <strong>Insertar salto de página</strong>.",
              "(Nota: La primera página ahora debe terminar con el sabor <strong>Galletas Jacker</strong>, y la siguiente página debe comenzar con el sabor de <strong>Chocolate con frambuesa</strong>).",
              "En la pestaña <strong>Vista</strong>, en el grupo <strong>Vistas de libro</strong>, haga clic en <strong>Normal</strong> para volver a la vista normal.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Copiar formato de A2:G2",
            description:
              "Use una función de Excel para copiar todo el formato del rango de celdas <strong>A2:G2</strong> ubicado en la hoja de trabajo <strong>Ganancias</strong> y aplique ese formato a la celda <strong>A2</strong> en la hoja de trabajo <strong>Costos</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Ganancias</strong>, seleccione el rango de celdas <strong>A2:G2</strong>.",
              "En la pestaña <strong>Inicio</strong>, en el grupo <strong>Portapapeles</strong>, haga clic en <strong>Copiar formato</strong>.",
              "(Nota: El cursor cambiará a un pincel).",
              "En la parte inferior de la hoja de trabajo, haga clic en la pestaña <strong>Costos</strong>.",
              "En la hoja de trabajo <strong>Costos</strong>, coloque el cursor del pincel en la celda <strong>A2</strong> y haga clic para aplicar el formato.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: PROMEDIO en A29",
            description:
              "En la hoja de trabajo <strong>Ganancias</strong>, ingrese una fórmula en la celda <strong>A29</strong> que utiliza una función de Excel para devolver los costos promedio de los sabores de masa de galletas en función de los valores en la columna <strong>Costo</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Ganancias</strong>, seleccione la celda <strong>A29</strong>.",
              "(Nota: Siempre coloque su fórmula en la celda donde desea que se muestre el resultado).",
              "En la Barra de fórmulas, escriba <strong>=PROMEDIO</strong>, luego presione la tecla <strong>Tab</strong> en su teclado.",
              "A la izquierda de la Barra de fórmulas, haga clic en <strong>fx</strong> para abrir el asistente <strong>Argumentos de función</strong>.",
              "En el asistente de Argumentos de funciones, configure lo siguiente: <strong>Número1: B4:B26</strong>.",
              "Haga clic en <strong>Aceptar</strong>.",
              "(Nota: La fórmula en la celda A29 debe ser <strong>=PROMEDIO(B4:B26)</strong>. El resultado mostrado debe ser <strong>1.73</strong>).",
            ],
            expectedResult: "1.73",
            finalFormula: "=PROMEDIO(B4:B26)",
          },
          {
            id: 4,
            title: "Tarea 4: Agregar Ganancia Total al gráfico",
            description:
              "En la hoja de trabajo <strong>Ganancias</strong>, incluya los datos de <strong>Ganancia Total</strong> en el cuadro <strong>Gastos, Ingresos, Ganancias y Sabor</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Ganancias</strong>, haga clic en el centro del gráfico para seleccionarlo.",
              "Haga clic en la pestaña <strong>Diseño de gráfico</strong>.",
              "En el grupo <strong>Datos</strong>, haga clic en <strong>Seleccionar datos</strong>.",
              "En la ventana emergente <strong>Seleccionar origen de datos</strong>, en el panel izquierdo de la ventana, haga clic en el cuadro <strong>Ganancia Total</strong>.",
              "Haga clic en <strong>Aceptar</strong>.",
              "(Nota: La <strong>Ganancia Total</strong> ahora debe agregarse a la leyenda que representa la tercera columna en el gráfico).",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Filtrar costos superiores al promedio",
            description:
              "Filtre la hoja de trabajo <strong>Ganancias</strong> para mostrar solo sabores con un <strong>Costo Superior del promedio</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Ganancias</strong>, haga clic en la flecha hacia abajo del autofiltro en el encabezado <strong>Costo</strong>.",
              "Desplácese sobre <strong>Filtros de número</strong> y haga clic en <strong>Superior del promedio</strong>.",
            ],
          },
        ],
      },
      {
        id: "project20",
        title: "Proyecto 4: Eventos & Banquetes",
        subtitle: "Hoja de cálculo de precios de alquiler",
        description:
          "Usted es el asistente administrativo de un hotel muy grande que alquila salas de banquetes. Est\u00E1 preparando una hoja de c\u00E1lculo de precios.",
        topics: [
          "Barra de herramientas de acceso rápido",
          "Propiedades del documento",
          "Área de impresión",
          "Guardar como página web",
          "Ajustar texto",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Agregar Abrir al acceso rápido",
            description:
              "Agregar <strong>Abrir</strong> a la <strong>Barra de herramientas de acceso rápido</strong>.",
            solution: [
              "Encima de la cinta, haga clic en la flecha hacia abajo <strong>Personalizar barra de herramientas de acceso rápido</strong>.",
              "Haga clic en <strong>Abrir</strong>.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Cambiar título del documento",
            description:
              "Agregue el valor <strong>Nuevas tarifas</strong> a la propiedad <strong>Título</strong> del documento.",
            solution: [
              "Haga clic en la pestaña <strong>Archivo</strong>.",
              "Localice la lista de <strong>Propiedades</strong> en el lado derecho de la página de <strong>Información</strong>.",
              "En el campo de la propiedad <strong>Título</strong>, escriba <strong>Nuevas tarifas</strong>.",
              "Haga clic en la flecha de regreso en la esquina superior izquierda de la ventana <strong>Información</strong> para volver a la hoja de trabajo.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Establecer área de impresión",
            description:
              "Establezca las celdas <strong>A1:F10</strong> para que sean las únicas celdas que se impriman.",
            solution: [
              "Seleccione el rango de celdas <strong>A1:F10</strong>.",
              "En la pestaña <strong>Disposición de página</strong>, en el grupo <strong>Configurar página</strong>, haga clic en <strong>Área de impresión</strong> y seleccione <strong>Establecer área de impresión</strong>.",
              "(Nota: Puede verificar la configuración del área de impresión en la pestaña <strong>Archivo</strong> seleccionando <strong>Imprimir</strong> y viéndola en el panel <strong>Vista previa</strong>).",
            ],
          },
          {
            id: 4,
            title: "Tarea 4: Guardar como página web",
            description:
              "Guarde la hoja de trabajo <strong>Salas</strong> como una página web llamada <strong>Precios_Alquiler.htm</strong> en la carpeta <strong>Gmetrix Templates</strong>.",
            solution: [
              "Seleccione la pestaña <strong>Archivo</strong>.",
              "En el panel izquierdo, seleccione <strong>Guardar como</strong> y vaya a la carpeta <strong>GMetrixTemplates</strong>.",
              "En la ventana emergente <strong>Guardar como</strong>, configure lo siguiente: <strong>Nombre de archivo:</strong> Precios_Alquiler, <strong>Guardar como tipo:</strong> página web (*.htm, *.html).",
              "En <strong>Guardar:</strong> seleccione <strong>Publicar</strong>.",
              "En el cuadro de diálogo <strong>Publicar como página web</strong>, seleccione <strong>Todo el contenido de Salas</strong>.",
              "Haga clic en <strong>Publicar</strong>.",
              "(Nota: Si se le solicita que siga usando el formato, haga clic en <strong>Sí</strong>).",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Ajustar texto en fila 2",
            description:
              "En la hoja de trabajo <strong>Salas</strong>, configure la fila de encabezado en la tabla (<strong>fila 2</strong>) para que las entradas más anchas que la columna se ajusten a varias líneas.",
            solution: [
              "En la hoja de trabajo <strong>Salas</strong>, seleccione la fila <strong>2</strong>.",
              "En la pestaña <strong>Inicio</strong>, en el grupo <strong>Alineación</strong>, haga clic en <strong>Ajustar texto</strong>.",
            ],
          },
        ],
      },
      {
        id: "project21",
        title: "Proyecto 5: Activos del Gobierno",
        subtitle: "Vehículos y artículos vendidos",
        description:
          "Usted es asistente de un administrador responsable de liquidar el excedente del gobierno. Se le ha encomendado preparar un libro de trabajo para estas subastas.",
        topics: [
          "Insertar columnas",
          "Dividir vistas",
          "Inmovilizar paneles",
          "Filas con bandas",
          "IZQUIERDA",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Insertar columna antes de A",
            description:
              "En la hoja de trabajo <strong>Vendido</strong>, inserte una nueva columna antes de la columna <strong>A</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Vendido</strong>, seleccione la columna <strong>A</strong> haciendo clic en la <strong>A</strong> en la parte superior de la columna.",
              "Haga clic derecho en la columna y seleccione <strong>Insertar</strong>.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Dividir la vista en la fila 7",
            description:
              "Divida la vista de la hoja de trabajo <strong>Vendido</strong> para que solo las filas 1 a 6 aparezcan en el panel superior y las filas restantes aparezcan en el panel inferior.",
            solution: [
              "En la hoja de trabajo <strong>Vendido</strong>, seleccione la fila <strong>7</strong>.",
              "(Nota: Al dividir las vistas, seleccione la fila debajo de la última fila que desea que aparezca en el panel superior).",
              "En la pestaña <strong>Vista</strong>, en el grupo <strong>Ventana</strong>, haga clic en <strong>Dividir</strong>.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Inmovilizar filas 1 a 3",
            description:
              "Configure la hoja de trabajo <strong>Vehículos</strong> para que las filas <strong>1 a 3</strong> permanezcan visibles mientras se desplaza verticalmente.",
            solution: [
              "En la hoja de trabajo <strong>Vehículos</strong>, seleccione la fila <strong>4</strong>.",
              "(Nota: Cuando congele fotogramas verticalmente, seleccione la fila debajo de la última fila que desea que permanezca visible).",
              "En la pestaña <strong>Vista</strong>, en el grupo <strong>Ventana</strong>, haga clic en la flecha hacia abajo <strong>Inmovilizar</strong> y seleccione <strong>Inmovilizar paneles</strong>.",
            ],
          },
          {
            id: 4,
            title: "Tarea 4: Activar filas con bandas",
            description:
              "En la hoja de trabajo <strong>Vendido</strong>, formatee la tabla para que cada fila se sombre de manera similar a la tabla en la hoja de trabajo <strong>Vehículos</strong>. Utilice una técnica que actualice automáticamente el formato si inserta una nueva fila.",
            solution: [
              "En la hoja de trabajo <strong>Vendido</strong>, haga clic en cualquier lugar dentro de la tabla para seleccionarla.",
              "En la pestaña contextual <strong>Diseño de tabla</strong>, en el grupo <strong>Opciones de estilo de tabla</strong>, haga clic en el cuadro <strong>Filas con bandas</strong> para habilitarlo.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Extraer código con IZQUIERDA",
            description:
              "En la hoja de trabajo <strong>Vehículos</strong>, inserte una función en la celda <strong>B4</strong> de la columna <strong>Código</strong> que extraiga las dos letras de la izquierda de la columna <strong>Estilo</strong> que se muestran en la celda <strong>D4</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Vehículos</strong>, seleccione la celda <strong>B4</strong>.",
              "(Nota: Siempre coloque su fórmula en la celda donde desea que se muestre el resultado).",
              "En la Barra de fórmulas, escriba <strong>=IZQUIERDA</strong>, luego presione la tecla <strong>Tab</strong> en su teclado.",
              "A la izquierda de la Barra de fórmulas, haga clic en <strong>fx</strong> para abrir el asistente <strong>Argumentos de función</strong>.",
              "En el asistente <strong>Argumentos de función</strong>, configure lo siguiente: <strong>Texto:</strong> D4, <strong>Núm_de_caracteres:</strong> 2.",
              "Haga clic en <strong>Aceptar</strong>.",
              "(Nota: El resultado debería ser <strong>HF</strong>).",
            ],
            expectedResult: "HF",
            finalFormula: "=IZQUIERDA([@[Lote#]],2)",
          },
        ],
      },
      {
        id: "project22",
        title: "Proyecto 6: Grill Móvil",
        subtitle: "Ingresos por ubicación en dos trimestres",
        description:
          "Usted y un socio poseen varias parrillas m\u00F3viles. Est\u00E1 analizando sus ingresos de los \u00FAltimos dos trimestres.",
        topics: [
          "Minigráficos",
          "Mover gráfico",
          "Contabilidad",
          "Cambiar fila/columna",
          "Mostrar fórmulas",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Crear minigráficos en F10:F14",
            description:
              "En la hoja de trabajo <strong>Trimestre 2</strong>, en la celda <strong>F10</strong>, inserte un minigráfico de columna que muestre la tendencia de ventas de Abril a Junio. Aplique <strong>Oro, Énfasis de estilo de minigráfico 2, más oscuro 25%</strong> al minigráfico. Copie el minigráfico a las celdas restantes en la columna (<strong>F11:F14</strong>).",
            solution: [
              "Seleccione la celda <strong>F10</strong> en la hoja de trabajo <strong>Trimestre 2</strong>.",
              "Seleccione la pestaña <strong>Insertar</strong>.",
              "En el grupo <strong>Minigráficos</strong>, haga clic en <strong>Columnas</strong>.",
              "En la ventana emergente <strong>Crear Minigráficos</strong>, configure los campos de la siguiente manera:\nRango de datos: <strong>B10:E10</strong>\nUbicación: <strong>$F$10</strong>",
              "Haga clic en <strong>Aceptar</strong>.",
              "En la pestaña <strong>Minigráfico</strong>, en el grupo <strong>Estilo</strong>, haga clic en la flecha hacia abajo <strong>Más</strong> para abrir la galería Estilo. Seleccione <strong>Oro, Énfasis de estilo de minigráfico 2, más oscuro 25%</strong>.",
              "En la esquina inferior derecha de la celda, haga clic y arrastre el controlador de relleno desde la celda <strong>F11</strong> hasta la celda <strong>F14</strong> para crear un minigráfico para cada ubicación.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Mover gráfico a hoja nueva",
            description:
              "Usando la función <strong>Mover gráfico</strong>, mueva el gráfico circular de la hoja de trabajo <strong>Trimestre 2</strong> a su propia hoja, con el nombre <strong>Gráfico Trimestre 2</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Trimestre 2</strong>, haga clic en cualquier lugar del gráfico circular para seleccionarlo.",
              "En la pestaña <strong>Diseño de gráfico</strong>, en el grupo <strong>Ubicación</strong>, haga clic en <strong>Mover gráfico</strong>.",
              "En la ventana emergente <strong>Mover gráfico</strong>, seleccione <strong>Hoja nueva:</strong> y escriba el nombre <strong>Gráfico Trimestre 2</strong>.",
              "Haga clic en <strong>Aceptar</strong>.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Aplicar formato contabilidad",
            description:
              "En la hoja de trabajo <strong>Trimestre 1</strong>, aplique un formato de número para mostrar los números en las columnas <strong>B</strong> a la <strong>E</strong> con dos decimales con el símbolo del dólar estadounidense (<strong>$</strong>) alineado a la izquierda y los puntos decimales también alineados.",
            solution: [
              "En la hoja de trabajo <strong>Trimestre 1</strong>, seleccione las columnas <strong>B</strong> a la <strong>E</strong>.",
              "En la pestaña <strong>Inicio</strong>, en el grupo <strong>Número</strong>, haga clic en el símbolo <strong>$</strong> (esto alineará los símbolos).",
              "(Nota: Como alternativa, haga clic en el iniciador del cuadro de diálogo en la esquina inferior derecha del grupo <strong>Número</strong>. En la pestaña <strong>Número</strong>, en el panel <strong>Categoría</strong>, seleccione <strong>Contabilidad</strong> y configure <strong>Posiciones decimales: 2</strong> y <strong>Símbolo: $</strong>. Luego haga clic en <strong>Aceptar</strong>).",
            ],
          },
          {
            id: 4,
            title: "Tarea 4: Cambiar fila/columna del gráfico",
            description:
              "En la hoja de trabajo <strong>Trimestre 1</strong>, cambie la configuración del gráfico <strong>Trimestre 1 Ingresos por ubicación</strong> para que muestre los meses en el eje-x y los ingresos en el eje-y.",
            solution: [
              "Haga clic en la pestaña de la hoja de trabajo <strong>Trimestre 1</strong> para seleccionarla.",
              "Haga clic en el gráfico para mostrar las pestañas <strong>Herramientas de gráfico</strong>.",
              "En la pestaña <strong>Diseño de gráfico</strong>, en el grupo <strong>Datos</strong>, haga clic en <strong>Cambiar fila/columna</strong>.",
            ],
          },
          {
            id: 5,
            title: "Tarea 5: Mostrar todas las fórmulas",
            description:
              "Muestre todas las fórmulas en la hoja de trabajo <strong>Trimestre 2</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Trimestre 2</strong>, seleccione la pestaña <strong>Fórmulas</strong>.",
              "En la pestaña <strong>Fórmulas</strong>, en el grupo <strong>Auditoría de fórmulas</strong>, haga clic en <strong>Mostrar fórmulas</strong>.",
            ],
          },
        ],
      },
      {
        id: "project23",
        title: "Proyecto 7: Soporte Técnico",
        subtitle: "Horas de empleados y tiempos de llamada",
        description:
          "Usted es el supervisor del servicio de asistencia tecnol\u00F3gica de su empresa. Utilice Excel para informar de las horas y llamadas de los empleados.",
        topics: [
          "Autocompletar",
          "Fila de totales",
          "SI",
          "MAYUSC",
          "Gráficos 3D",
        ],
        tasks: [
          {
            id: 1,
            title: "Tarea 1: Copiar minigráfico en Tendencia",
            description:
              "En la hoja de trabajo <strong>Tiempo promedio de llamada</strong>, use una función de Excel para copiar el minigráfico en todas las celdas vacías de la columna <strong>Tendencia</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Tiempo promedio de llamada</strong>, seleccione la celda <strong>G4</strong>.",
              "En la esquina inferior derecha de la celda <strong>G4</strong>, haga clic y arrastre el controlador de relleno hacia abajo en la columna <strong>Tendencia</strong> que comienza con la celda <strong>G4</strong> y termina con la celda <strong>G16</strong>.",
            ],
          },
          {
            id: 2,
            title: "Tarea 2: Agregar fila de totales",
            description:
              "En la hoja de trabajo <strong>Horas de empleados</strong>, agregue una fila a la tabla que calcule automáticamente las horas totales trabajadas por todos los empleados.",
            solution: [
              "En la hoja de trabajo <strong>Horas de empleados</strong>, haga clic en cualquier celda de la tabla para seleccionar la tabla.",
              "En la pestaña <strong>Diseño de tabla</strong>, en el grupo <strong>Opciones de estilo de tabla</strong>, haga clic en la <strong>Fila de totales</strong> para habilitarla.",
            ],
          },
          {
            id: 3,
            title: "Tarea 3: Función en H4",
            description:
              "Agregue una función a la columna <strong>Horas extras</strong> en la celda <strong>H4</strong> en la hoja de trabajo <strong>Horas de empleados</strong> que muestre la palabra <strong>Sí</strong> si el valor en la celda <strong>J4</strong> es mayor que 40. De lo contrario, muestre la palabra <strong>No</strong>.",
            solution: [
              "En la hoja de trabajo <strong>Horas de empleados</strong>, haga clic en la celda <strong>H4</strong>.",
              "En la <strong>Barra de fórmulas</strong>, escriba <strong>=SI</strong>, luego presione la tecla de tab en su teclado.",
              "A la izquierda de la <strong>Barra de fórmulas</strong>, haga clic en <strong>fx</strong> para abrir el asistente <strong>Argumentos de función</strong>.",
              "En el asistente de <strong>Argumentos de función</strong>, configure lo siguiente:\n<strong>Prueba_lógica:</strong> J4> 40\n<strong>Valor_si_verdadero:</strong> Sí\n<strong>Valor_si_falso:</strong> No",
              "Haga clic en <strong>Aceptar</strong>.",
            ],
            finalFormula: '=SI(J4>40,"Sí","No")',
          },
          {
            id: 4,
            title: "Tarea 4: Función en I4",
            description:
              "En la celda <strong>I4</strong> de la hoja <strong>Horas de empleados</strong>, use una función para copiar el nombre de la celda <strong>A4</strong> y dele formato al nombre para que todas las letras estén en mayúsculas.",
            solution: [
              "En la hoja de trabajo <strong>Horas de empleados</strong>, seleccione la celda <strong>I4</strong>.\n(Nota: Siempre coloque su fórmula en la celda donde desea que se muestre el resultado).",
              "En la <strong>Barra de fórmulas</strong>, escriba <strong>=MAYUSC</strong>, luego presione la tecla de tab en su teclado.",
              "A la izquierda de la <strong>Barra de fórmulas</strong>, haga clic en <strong>fx</strong> para abrir el asistente <strong>Argumentos de función</strong>.",
              "En el asistente de <strong>Argumentos de función</strong>, configure lo siguiente:\n<strong>Texto:</strong> A4",
              "Haga clic en <strong>Aceptar</strong>.\n(Nota: El resultado que se muestra debe ser <strong>EMILIO</strong>).",
            ],
            expectedResult: "EMILIO",
            finalFormula: "=MAYUSC(A4)",
          },
          {
            id: 5,
            title: "Tarea 5: Crear gráfico de viernes",
            description:
              "En la hoja de trabajo <strong>Tiempo promedio de llamada</strong>, cree un gráfico de <strong>Columnas agrupadas 3D</strong> que muestre solo el <strong>Tiempo de llamada</strong> para el <strong>Viernes</strong> por cada <strong>Vendedor</strong>. Coloque el nuevo gráfico a la derecha de la tabla.",
            solution: [
              "En la hoja de trabajo <strong>Tiempo promedio de llamada</strong>, haga clic en la pestaña <strong>Insertar</strong>.",
              "En el grupo <strong>Gráficos</strong>, haga clic en el icono <strong>Insertar gráfico de columnas</strong> y seleccione <strong>Columna agrupada 3D</strong>.",
              "En la pestaña <strong>Diseño de gráfico</strong>, en el grupo <strong>Datos</strong>, haga clic en <strong>Seleccionar datos</strong>.",
              "Ingrese el <strong>Rango de datos del gráfico:</strong> <strong>=Partes[Vie]</strong> y haga clic en la flecha al final del campo dos veces.",
              "En <strong>Etiquetas de eje horizontal (categoría)</strong>, haga clic en <strong>Editar</strong>. Luego seleccione las celdas <strong>A4</strong> a <strong>A16</strong> y haga clic en <strong>Aceptar</strong> dos veces.",
              "Mueva el gráfico al lado derecho de la tabla.",
            ],
          },
        ],
      },
    ],
  },
];
